import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  List,
  Avatar,
  Input,
  Space,
  Typography,
  Card,
  Empty,
  Spin,
  Tabs,
  Row,
  Col,
  Badge,
  Image,
  message,
  Modal,
  Button,
  Tooltip,
  Popover,
  Popconfirm,
} from 'antd';
import {
  UserOutlined,
  SearchOutlined,
  WechatOutlined,
  TeamOutlined,
  FileImageOutlined,
  EnvironmentOutlined,
  SoundOutlined,
  VideoCameraOutlined,
  TransactionOutlined,
  GiftOutlined,
  FileOutlined,
  LinkOutlined,
  DownloadOutlined,
  MessageOutlined,
  ReloadOutlined,
  EyeOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useRequest, useDebounceFn } from 'ahooks';
import { useCompany } from '../../hooks/company';
import {
  getWechatUsers,
  getWechatContacts,
  getWechatChatrooms,
  getWechatMessages,
  getWechatFiles,
  deleteWechatUser,
  WechatUserDTO,
  WechatContactDTO,
  WechatChatroomDTO,
  WechatMessageDTO,
  WechatFileDTO,
} from '../../apis/api';
import { apiHost } from '../../apis/request';
import { ListUserItemDTO, auditController, systemController, WechatContactDTO as ApiWechatContactDTO } from '../../api';
import dayjs from 'dayjs';
import { WechatImagePreview, WechatVoicePreview } from '../media/CompanyMedia';
import AuditUserList from '../../components/AuditUserList';
import './audit.scss';

const { Text } = Typography;
const { Search } = Input;
const { TabPane } = Tabs;

export default function WechatManage() {
  const company = useCompany();
  const [selectedUser, setSelectedUser] = useState<ListUserItemDTO | null>(null);
  const [selectedWxUser, setSelectedWxUser] = useState<WechatUserDTO | null>(
    null,
  );
  const [selectedContact, setSelectedContact] = useState<WechatContactDTO | null>(
    null,
  );
  const [selectedChatroom, setSelectedChatroom] = useState<WechatChatroomDTO | null>(
    null,
  );
  const [activeWxTab, setActiveWxTab] = useState<string>(''); // 微信账号Tab
  
  // 刷新状态：用于跟踪正在刷新/加载的Tab
  const [refreshingTabs, setRefreshingTabs] = useState<Set<string>>(new Set());

  // 联系人分页状态
  const [contactsPage, setContactsPage] = useState(0); // 当前页码（从0开始）
  const [contactsHasMore, setContactsHasMore] = useState(true); // 是否还有更多数据
  const [contactsLoading, setContactsLoading] = useState(false); // 是否正在加载更多
  const [contactsTotal, setContactsTotal] = useState(0); // 联系人总数
  const [contactsKeyword, setContactsKeyword] = useState(''); // 搜索关键词
  const [allContactsData, setAllContactsData] = useState<WechatContactDTO[]>([]); // 所有已加载的联系人数据
  const contactListRef = useRef<HTMLDivElement>(null); // 联系人列表容器引用
  const CONTACTS_PAGE_SIZE = 100; // 每页100条

  // 转换函数：将 API 返回的 WechatContactDTO 转换为组件使用的格式
  const convertApiContactToContact = useCallback((apiContact: ApiWechatContactDTO): WechatContactDTO => {
    return {
      id: typeof apiContact.id === 'number' ? apiContact.id : Number(apiContact.id) || 0,
      userId: typeof apiContact.userId === 'number' ? apiContact.userId : Number(apiContact.userId) || 0,
      wxid: apiContact.wxid || '',
      username: apiContact.username || '',
      nickname: apiContact.nickname || '',
      type: apiContact.type || 0,
      alias: apiContact.conRemark || '',
      avatar: apiContact.avatar || '',
      raw: apiContact.raw || '',
      createTime: apiContact.createTime || '',
      createdAt: apiContact.createdAt || '',
      updatedAt: apiContact.updatedAt || '',
      user: apiContact.user || {} as any,
    };
  }, []);

  // 消息容器引用，用于自动滚动到底部
  const messageContainerRef = useRef<HTMLDivElement>(null);
  
  // 文件缓存：key 为 msgSvrId，value 为文件信息
  const fileCache = useRef<Map<string, WechatFileDTO>>(new Map());
  
  // 正在加载的文件列表
  const loadingFiles = useRef<Set<string>>(new Set());
  
  // 待加载的文件队列
  const pendingFiles = useRef<string[]>([]);
  
  // 用于触发重新渲染的状态
  const [fileLoadTrigger, setFileLoadTrigger] = useState(0);

  // 微信 DB 删除消息相关状态
  const [batchDeleteModalOpen, setBatchDeleteModalOpen] = useState(false);
  const [batchDeleteMsgIds, setBatchDeleteMsgIds] = useState('');
  const [batchDeleteSubmitting, setBatchDeleteSubmitting] = useState(false);
  
  // IntersectionObserver 引用
  const observerRef = useRef<IntersectionObserver | null>(null);
  
  // 存储所有文件消息的元素引用
  const fileMessageRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // 请求取消控制器
  const contactsAbortControllerRef = useRef<AbortController | null>(null);
  const chatroomsAbortControllerRef = useRef<AbortController | null>(null);
  const requestSequenceRef = useRef(0);

  // 用于防止滚动时重复触发联系人加载的 ref
  const isLoadingMoreContactsRef = useRef(false);

  // 获取微信账号列表
  const {
    data: wxUsers,
    loading: loadingWxUsers,
    run: loadWxUsers,
    mutate: mutateWxUsers,
  } = useRequest(
    async (userId: number) => {
      if (!company?.id) return [];
      return await getWechatUsers(company.id, userId);
    },
    {
      manual: true,
    },
  );

  // 获取微信联系人列表 - 改为分页加载
  const {
    loading: loadingContacts,
    run: loadContactsPage,
    cancel: cancelLoadContacts,
  } = useRequest(
    async (userId: number, wxid: string, page: number, keyword: string, isLoadMore: boolean = false) => {
      if (!company?.id) return [];
      console.log(`🚀 开始请求联系人列表 - 第${page}页, 关键词: ${keyword || '无'}`);
      const startTime = performance.now();

      const result = await auditController.getWechatContactByUserIdAndWxidWithPage(
        company.id,
        userId,
        wxid,
        keyword || '',
        page,
        CONTACTS_PAGE_SIZE
      );

      const endTime = performance.now();
      const apiContacts = result.list || [];
      const total = result.total || 0;
      console.log(`✅ 联系人列表请求完成: ${(endTime - startTime).toFixed(2)}ms, 数量: ${apiContacts.length}, 总数: ${total}`);

      // 转换 API 数据为组件使用的格式
      const contacts = apiContacts.map(convertApiContactToContact);

      // 处理数据拼接
      let allLoadedContacts: WechatContactDTO[];
      if (isLoadMore) {
        // 加载更多，拼接数据
        allLoadedContacts = [...(allContactsData || []), ...contacts];
        setAllContactsData(allLoadedContacts);
      } else {
        // 初始加载，替换数据
        allLoadedContacts = contacts;
        setAllContactsData(contacts);
      }

      // 更新分页状态
      setContactsPage(page);
      setContactsTotal(total);
      // 判断是否还有更多数据：已加载数量 < 总数量
      const hasMore = allLoadedContacts.length < total;
      setContactsHasMore(hasMore);
      setContactsLoading(false);

      console.log(`📊 分页状态: 已加载 ${allLoadedContacts.length}/${total}, 还有更多: ${hasMore}`);

      return contacts;
    },
    {
      manual: true,
    },
  );
  
  // 初始加载联系人
  const loadContacts = useCallback(async (userId: number, wxid: string, keyword: string = '') => {
    setContactsPage(0);
    setContactsHasMore(true);
    setContactsTotal(0);
    setAllContactsData([]);
    await loadContactsPage(userId, wxid, 0, keyword, false);
  }, [loadContactsPage]);

  // 加载更多联系人
  const loadMoreContacts = useCallback(async (userId: number, wxid: string, keyword: string = '') => {
    // 使用 ref 防止重复调用
    if (isLoadingMoreContactsRef.current || contactsLoading || !contactsHasMore) {
      console.log('⏸️ 跳过加载更多（正在加载中或没有更多数据）', {
        isLoadingMoreContactsRef: isLoadingMoreContactsRef.current,
        contactsLoading,
        contactsHasMore,
      });
      return;
    }

    isLoadingMoreContactsRef.current = true;
    setContactsLoading(true);
    const nextPage = contactsPage + 1;
    console.log(`📄 加载更多联系人 - 第${nextPage}页`);

    try {
      await loadContactsPage(userId, wxid, nextPage, keyword, true);
    } finally {
      isLoadingMoreContactsRef.current = false;
    }
  }, [contactsPage, contactsHasMore, contactsLoading, contactsKeyword, loadContactsPage]);

  // 防抖的滚动处理函数
  const { run: handleContactListScroll } = useDebounceFn(
    (scrollTop: number, scrollHeight: number, clientHeight: number) => {
      const distanceToBottom = scrollHeight - scrollTop - clientHeight;

      console.log('📜 联系人列表滚动事件 (防抖后)', {
        scrollTop,
        scrollHeight,
        clientHeight,
        distanceToBottom,
        contactsHasMore,
        contactsLoading,
        isLoadingMoreContactsRef: isLoadingMoreContactsRef.current,
        contactsPage,
      });

      // 当滚动到距离底部50px时，且还有更多数据时，加载更多
      if (distanceToBottom < 50 && contactsHasMore && !contactsLoading && !isLoadingMoreContactsRef.current && selectedUser && selectedWxUser) {
        console.log('✅ 触发后端分页加载更多 (防抖后)', {
          当前页: contactsPage,
          是否有更多: contactsHasMore,
          正在加载: contactsLoading,
        });
        loadMoreContacts(selectedUser.id as number, selectedWxUser.wxid, contactsKeyword);
      }
    },
    { wait: 100 } // 100ms 防抖
  );


  // 获取微信群组列表
  const {
    data: chatrooms,
    loading: loadingChatrooms,
    run: loadChatrooms,
    cancel: cancelLoadChatrooms,
    mutate: mutateChatrooms,
  } = useRequest(
    async (userId: number, wxid: string) => {
      if (!company?.id) return [];
      console.log('🚀 开始请求群组列表');
      const startTime = performance.now();
      const result = await getWechatChatrooms(company.id, userId, wxid);
      const endTime = performance.now();
      console.log(`✅ 群组列表请求完成: ${(endTime - startTime).toFixed(2)}ms, 数量: ${result?.length || 0}`);
      return result;
    },
    {
      manual: true,
      debounceWait: 300, // 防抖300ms
    },
  );

  // 消息列表状态
  const [messages, setMessages] = useState<WechatMessageDTO[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const isLoadingMoreRef = useRef(false);
  const isInitialLoadRef = useRef(true); // 标记是否是初始加载
  
  // 获取微信消息（初始加载）
  const {
    loading: loadingMessages,
    run: loadMessages,
  } = useRequest(
    async (userId: number, wxid: string, talker: string, createTime?: number) => {
      if (!company?.id) return [];
      const isLoadMore = createTime !== undefined;

      // 记录加载前的滚动位置和高度
      let previousScrollTop = 0;
      let previousScrollHeight = 0;
      if (isLoadMore && messageContainerRef.current) {
        previousScrollTop = messageContainerRef.current.scrollTop;
        previousScrollHeight = messageContainerRef.current.scrollHeight;
      }
      
      if (isLoadMore) {
        isLoadingMoreRef.current = true;
        setLoadingMore(true);
      }
      const data = await getWechatMessages(company.id, userId, wxid, talker, createTime);
      
      if (isLoadMore) {
        // 加载更多：将新数据拼接到现有数据前面（因为消息是倒序的）
        setMessages(prev => [...prev, ...data]);
        // 如果返回的数据少于100条，说明没有更多了
        setHasMore(data.length >= 100);
        setLoadingMore(false);
        isLoadingMoreRef.current = false;
        
        // 保持滚动位置：在新内容加载后，调整滚动位置
        setTimeout(() => {
          if (messageContainerRef.current) {
            const newScrollHeight = messageContainerRef.current.scrollHeight;
            const heightDiff = newScrollHeight - previousScrollHeight;
            messageContainerRef.current.scrollTop = previousScrollTop + heightDiff;
          }
        }, 0);
      } else {
        // 初始加载：直接设置数据
        setMessages(data);
        setHasMore(data.length >= 100);
        isInitialLoadRef.current = true; // 标记为初始加载
      }
      
      return data;
    },
    {
      manual: true,
    },
  );

  // 格式化文件大小
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // 获取文件下载 URL
  const getFileDownloadUrl = (userId: number, key: string) => {
    if (!company?.id) return '';
    return `${apiHost}/company/${company.id}/audit/wechat_file/download?userId=${userId}&key=${encodeURIComponent(key)}`;
  };

  // 获取图片 URL（使用 OSS URL）
  const getImageUrl = (key: string) => {
    if (!company?.id) return '';
    return `${apiHost}/company/${company.id}/oss_url?path=${encodeURIComponent(key)}`;
  };

  // 批量加载文件
  const batchLoadFiles = useCallback(async (msgSvrIds: string[]) => {
    if (!company?.id || !selectedUser || !selectedWxUser || msgSvrIds.length === 0) {
      console.warn('⚠️ 批量加载文件失败：缺少必要参数', {
        hasCompany: !!company?.id,
        hasUser: !!selectedUser,
        hasWxUser: !!selectedWxUser,
        msgSvrIdsCount: msgSvrIds.length,
      });
      return;
    }

    // 过滤掉已经在缓存中或正在加载的
    const idsToLoad = msgSvrIds.filter(
      id => !fileCache.current.has(id) && !loadingFiles.current.has(id)
    );

    if (idsToLoad.length === 0) {
      console.log('✅ 所有文件已在缓存或正在加载中，跳过批量加载');
      return;
    }

    console.log('🚀 开始批量加载文件:', {
      请求数量: idsToLoad.length,
      msgSvrIds: idsToLoad,
    });

    // 标记为正在加载
    idsToLoad.forEach(id => loadingFiles.current.add(id));

    try {
      const files = await getWechatFiles(
        company.id,
        selectedUser.id as number,
        selectedWxUser.wxid,
        idsToLoad,
      );

      console.log('📥 批量加载文件响应:', {
        请求数量: idsToLoad.length,
        返回数量: files?.length || 0,
        files: files?.map(f => ({ msgSvrId: f.msgSvrId, hasUserFile: !!f.userFile })),
      });

      if (files && files.length > 0) {
        files.forEach(file => {
          fileCache.current.set(file.msgSvrId.toString(), file);
          console.log('💾 文件已缓存:', file.msgSvrId, 'key:', file.key);
        });
        
        // 触发重新渲染以显示加载的文件
        setFileLoadTrigger(prev => prev + 1);
        console.log('🔄 触发重新渲染，显示加载的文件');
      }
    } catch (error) {
      console.error('❌ 批量加载文件失败:', error);
    } finally {
      // 移除加载标记
      idsToLoad.forEach(id => loadingFiles.current.delete(id));
      console.log('✅ 批量加载完成，移除加载标记');
    }
  }, [company?.id, selectedUser, selectedWxUser]);

  // 防抖的批量加载触发器
  const batchLoadTimer = useRef<NodeJS.Timeout>();
  const triggerBatchLoad = useCallback(() => {
    if (batchLoadTimer.current) {
      clearTimeout(batchLoadTimer.current);
    }

    console.log('⏱️ 触发批量加载，待加载队列长度:', pendingFiles.current.length);

    batchLoadTimer.current = setTimeout(() => {
      if (pendingFiles.current.length > 0) {
        // 每次最多加载10条
        const toLoad = pendingFiles.current.splice(0, 10);
        console.log('📤 执行批量加载，本次加载数量:', toLoad.length, '剩余:', pendingFiles.current.length);
        batchLoadFiles(toLoad);
      } else {
        console.log('ℹ️ 待加载队列为空，跳过批量加载');
      }
    }, 100); // 100ms 防抖
  }, [batchLoadFiles]);

  // 当消息加载完成后，只在初始加载时自动滚动到底部
  useEffect(() => {
    if (messages && messages.length > 0 && messageContainerRef.current && isInitialLoadRef.current) {
      // 使用 setTimeout 确保 DOM 已更新
      setTimeout(() => {
        if (messageContainerRef.current) {
          messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
          isInitialLoadRef.current = false; // 初始加载完成，重置标记
        }
      }, 100);
    }
  }, [messages]);

  // 用户点击的实际执行函数
  const doUserClick = async (user: ListUserItemDTO) => {
    // 如果正在加载，取消之前的请求
    if (loadingContacts) {
      cancelLoadContacts();
    }
    if (loadingChatrooms) {
      cancelLoadChatrooms();
    }

    // 如果是同一个用户，不重复加载
    if (selectedUser?.id === user.id) {
      return;
    }

    // 生成新的请求序列号
    requestSequenceRef.current += 1;

    // 立即清空所有旧数据，避免显示之前的内容
    mutateWxUsers(undefined);
    setAllContactsData([]);
    setContactsPage(1);
    setContactsHasMore(true);
    mutateChatrooms(undefined);
    setSelectedUser(user);
    setSelectedWxUser(null);
    setSelectedContact(null);
    setSelectedChatroom(null);
    setActiveWxTab('');
    setMessages([]);

    try {
      await loadWxUsers(user.id as number);
    } catch (error) {
      console.error('加载微信账号失败:', error);
    }
  };

  // 用户点击防抖函数（300ms）
  const { run: handleUserClick } = useDebounceFn(doUserClick, {
    wait: 300,
  });

  // 当微信账号列表加载完成后，自动选择第一个账号
  useEffect(() => {
    const loadFirstAccount = async () => {
      if (wxUsers && wxUsers.length > 0 && selectedUser && !activeWxTab) {
        const firstWxUser = wxUsers[0];
        setActiveWxTab(firstWxUser.wxid);
        setSelectedWxUser(firstWxUser);

        // 生成新的请求序列号
        requestSequenceRef.current += 1;
        const currentSequence = requestSequenceRef.current;

        // 标记为正在加载
        setRefreshingTabs(prev => new Set(prev).add(firstWxUser.wxid));

        try {
          // 并行请求两个接口
          await Promise.all([
            loadContacts(selectedUser.id as number, firstWxUser.wxid, contactsKeyword),
            loadChatrooms(selectedUser.id as number, firstWxUser.wxid)
          ]);

          // 检查请求序列号
          if (currentSequence !== requestSequenceRef.current) {
            console.log('忽略过期的第一个账号加载结果');
            return;
          }
        } catch (error) {
          // 如果是取消错误，不显示错误信息
          if (error && typeof error === 'object' && 'name' in error && error.name === 'CanceledError') {
            console.log('第一个账号加载请求已被取消');
            return;
          }
          console.error('加载第一个账号数据失败:', error);
        } finally {
          // 只有当前序列号匹配时才移除加载标记
          if (currentSequence === requestSequenceRef.current) {
            setRefreshingTabs(prev => {
              const next = new Set(prev);
              next.delete(firstWxUser.wxid);
              return next;
            });
          }
        }
      }
    };

    loadFirstAccount();
  }, [wxUsers, selectedUser]);

  // 组件卸载或用户切换时，取消所有待处理的请求
  useEffect(() => {
    return () => {
      // 清理时取消所有请求
      if (loadingContacts) {
        cancelLoadContacts();
      }
      if (loadingChatrooms) {
        cancelLoadChatrooms();
      }
    };
  }, [selectedUser]); // 当选中用户变化时，触发清理

  // 联系人列表滚动监听已移至 JSX 的 onScroll 事件中

  // 当消息加载完成后，预加载最近10条文件类型的消息
  useEffect(() => {
    if (messages && messages.length > 0) {
      // 文件类型：3-图片, 6-文件, 34-语音, 43-视频
      const fileTypes = [3, 6, 34, 43];
      
      // 反转后取前10条（最新的10条）
      const reversedMessages = [...messages].reverse();
      
      // 过滤文件类型消息（包括 type 49 中的 type 6 文件）
      const fileMessages = reversedMessages.filter(msg => {
        if (fileTypes.includes(msg.type)) {
          return true;
        }
        // 检查 type 49 中的文件类型（type 6）
        if (msg.type === 49 && msg.content && msg.content.trim().startsWith('<?xml')) {
          try {
            const xmlData = parseXmlMessage(msg.content);
            return xmlData && xmlData.type === 6;
          } catch {
            return false;
          }
        }
        return false;
      });
      
      // 取最近的10条文件消息
      const recentFileMessages = fileMessages.slice(0, 10);
      const msgSvrIds = recentFileMessages.map(msg => msg.msgSvrId.toString());
      
      console.log('📦 预加载文件消息:', {
        总消息数: messages.length,
        文件消息数: fileMessages.length,
        预加载数量: msgSvrIds.length,
        msgSvrIds,
      });
      
      if (msgSvrIds.length > 0) {
        // 立即批量加载这些文件
        batchLoadFiles(msgSvrIds);
      }
    }
  }, [messages, batchLoadFiles]);

  // 初始化 Intersection Observer
  useEffect(() => {
    // 确保消息容器已经渲染
    if (!messageContainerRef.current) {
      console.warn('⚠️ 消息容器未渲染，延迟初始化 Observer');
      return;
    }

    // 如果已有 observer，先断开
    if (observerRef.current) {
      observerRef.current.disconnect();
      console.log('🔍 断开旧的 Observer');
    }

    // 创建 Intersection Observer
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // 元素进入视口
            const msgSvrId = entry.target.getAttribute('data-msg-svr-id');
            const msgType = entry.target.getAttribute('data-msg-type');
            
            console.log('📸 检测到文件消息进入视口:', {
              msgSvrId,
              msgType,
              hasCache: fileCache.current.has(msgSvrId || ''),
              isLoading: loadingFiles.current.has(msgSvrId || ''),
            });
            
            if (msgSvrId && !fileCache.current.has(msgSvrId) && !loadingFiles.current.has(msgSvrId)) {
              console.log('✅ 触发文件加载:', msgSvrId, '类型:', msgType);
              
              // 该文件还没有加载，添加到待加载队列
              if (!pendingFiles.current.includes(msgSvrId)) {
                pendingFiles.current.push(msgSvrId);
                console.log('📋 添加到待加载队列，当前队列长度:', pendingFiles.current.length);
              }
              // 触发批量加载
              triggerBatchLoad();
            } else if (msgSvrId && fileCache.current.has(msgSvrId)) {
              console.log('✅ 文件已在缓存中，跳过加载:', msgSvrId);
            }
          }
        });
      },
      {
        root: messageContainerRef.current,
        rootMargin: '100px', // 提前100px开始加载
        threshold: 0.1,
      }
    );

    console.log('🔍 Intersection Observer 已初始化，root:', messageContainerRef.current);

    // 延迟一下，确保所有文件消息元素都已渲染，然后重新观察所有元素
    setTimeout(() => {
      if (observerRef.current && messageContainerRef.current) {
        // 重新观察所有已注册的文件消息元素
        fileMessageRefs.current.forEach((element, msgSvrId) => {
          if (element && observerRef.current) {
            observerRef.current.observe(element);
            console.log('🔄 重新观察文件消息:', msgSvrId);
          }
        });
        console.log('✅ 已重新观察所有文件消息，总数:', fileMessageRefs.current.size);
      }
    }, 200);

    return () => {
      // 清理 observer
      if (observerRef.current) {
        observerRef.current.disconnect();
        console.log('🔍 Intersection Observer 已断开');
      }
    };
  }, [messages, triggerBatchLoad, selectedContact, selectedChatroom]); // 依赖 messages 和选中状态确保容器已渲染

  // 切换对话时，清除文件缓存和observer
  useEffect(() => {
    fileCache.current.clear();
    loadingFiles.current.clear();
    pendingFiles.current = [];
    
    // 断开所有已观察的元素
    if (observerRef.current) {
      observerRef.current.disconnect();
    }
    
    // 清空元素引用
    fileMessageRefs.current.clear();
  }, [selectedContact, selectedChatroom]);

  // 在消息列表渲染完成后，确保所有文件消息元素都被观察
  useEffect(() => {
    if (!messages || messages.length === 0 || !observerRef.current) {
      return;
    }

    // 延迟执行，确保 DOM 已完全渲染
    const timer = setTimeout(() => {
      if (observerRef.current && messageContainerRef.current) {
        // 查找所有带有 data-msg-svr-id 属性的文件消息元素
        const fileElements = messageContainerRef.current.querySelectorAll('[data-msg-svr-id]');
        console.log('🔍 在消息容器中找到文件消息元素:', fileElements.length);
        
        fileElements.forEach((element) => {
          const msgSvrId = element.getAttribute('data-msg-svr-id');
          if (msgSvrId && element instanceof HTMLDivElement) {
            // 如果还没有注册，则注册并观察
            if (!fileMessageRefs.current.has(msgSvrId)) {
              fileMessageRefs.current.set(msgSvrId, element);
              observerRef.current?.observe(element);
              console.log('✅ 自动注册并观察文件消息:', msgSvrId);
            } else {
              // 如果已注册，确保正在被观察
              observerRef.current?.observe(element);
            }
          }
        });
        
        console.log('✅ 文件消息观察完成，总数:', fileMessageRefs.current.size);
      }
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [messages, fileLoadTrigger]); // 依赖 fileLoadTrigger 确保文件加载后重新检查

  // 加载更多消息
  const loadMoreMessages = useCallback(() => {
    console.log('📜 loadMoreMessages 被调用，检查条件:', {
      hasMore,
      loadingMore,
      loadingMessages,
      selectedUser: !!selectedUser,
      selectedWxUser: !!selectedWxUser,
      messagesLength: messages.length,
    });

    if (!hasMore) {
      console.log('❌ 无法加载更多：hasMore 为 false');
      return;
    }
    if (loadingMore) {
      console.log('❌ 无法加载更多：正在加载更多中');
      return;
    }
    if (loadingMessages) {
      console.log('❌ 无法加载更多：正在加载消息中');
      return;
    }
    if (!selectedUser || !selectedWxUser) {
      console.log('❌ 无法加载更多：未选择用户或微信账号');
      return;
    }

    // 获取当前消息列表中的最后一条消息（最旧的消息）的 createTime
    // messages 数组是倒序的（最新的在前），所以最后一条是最旧的
    if (messages.length === 0) {
      console.log('❌ 无法加载更多：消息列表为空');
      return;
    }

    const oldestMessage = messages[messages.length - 1]; // 最后一条是最旧的
    // createTime 是 string 类型（如 "2025-11-10T22:46:17"），需要转换为时间戳（number）
    const createTime = new Date(oldestMessage.createTime).getTime();
    
    console.log('📜 ✅ 准备加载更多消息:', {
      oldestMessageCreateTime: oldestMessage.createTime,
      createTimeTimestamp: createTime,
      oldestMessageIndex: messages.length - 1,
      totalMessages: messages.length,
    });

    const talker = selectedContact?.username || selectedChatroom?.chatroomname;
    if (talker) {
      console.log('📜 ✅ 调用 loadMessages，参数:', {
        userId: selectedUser.id,
        wxid: selectedWxUser.wxid,
        talker,
        createTime,
      });
      loadMessages(
        selectedUser.id as number,
        selectedWxUser.wxid,
        talker,
        createTime,
      );
    } else {
      console.log('❌ 无法加载更多：talker 为空');
    }
  }, [hasMore, loadingMore, loadingMessages, selectedUser, selectedWxUser, selectedContact, selectedChatroom, messages, loadMessages]);

  // 添加滚动事件监听器（作为备用方案）
  useEffect(() => {
    const container = messageContainerRef.current;
    if (!container) return;

    let scrollTimer: NodeJS.Timeout;
    const handleScroll = () => {
      // 防抖处理
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        if (!container) return;

        // 检测滚动到顶部，加载更多消息
        const scrollTop = container.scrollTop;
        const scrollHeight = container.scrollHeight;
        const clientHeight = container.clientHeight;
        
        // 调试信息（只在接近顶部时输出，避免日志过多）
        if (scrollTop <= 100) {
          const canLoadMore = scrollTop <= 50 && hasMore && !loadingMore && !loadingMessages && messages.length > 0;
          console.log('📜 滚动检测（接近顶部）:', {
            scrollTop,
            scrollHeight,
            clientHeight,
            hasMore,
            loadingMore,
            loadingMessages,
            messagesLength: messages.length,
            canLoadMore,
            conditions: {
              scrollTopCondition: scrollTop <= 50,
              hasMoreCondition: hasMore,
              notLoadingMore: !loadingMore,
              notLoadingMessages: !loadingMessages,
              hasMessages: messages.length > 0,
            },
          });
          
          // 当滚动到顶部附近（距离顶部50px内）时，加载更多
          // 注意：由于消息列表使用了 reverse()，顶部显示的是最旧的消息
          if (canLoadMore) {
            console.log('📜 ✅ 滚动到顶部，触发加载更多消息');
            loadMoreMessages();
          } else if (scrollTop <= 50) {
            console.log('📜 ⚠️ 滚动到顶部但条件不满足:', {
              scrollTop: scrollTop <= 50,
              hasMore,
              loadingMore,
              loadingMessages,
              messagesLength: messages.length,
            });
          }
        }

        if (!observerRef.current) return;

        // 检查视口内可见的文件消息
        const fileElements = container.querySelectorAll('[data-msg-svr-id]');
        console.log('📜 滚动检测，找到文件消息元素:', fileElements.length);

        fileElements.forEach((element) => {
          if (!(element instanceof HTMLElement)) return;

          const rect = element.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          
          // 检查元素是否在容器视口内（包括提前100px）
          const isVisible = 
            rect.top < containerRect.bottom + 100 &&
            rect.bottom > containerRect.top - 100 &&
            rect.left < containerRect.right &&
            rect.right > containerRect.left;

          if (isVisible) {
            const msgSvrId = element.getAttribute('data-msg-svr-id');
            const msgType = element.getAttribute('data-msg-type');
            
            if (msgSvrId && !fileCache.current.has(msgSvrId) && !loadingFiles.current.has(msgSvrId)) {
              console.log('📜 滚动检测到未加载的文件:', msgSvrId, '类型:', msgType);
              
              if (!pendingFiles.current.includes(msgSvrId)) {
                pendingFiles.current.push(msgSvrId);
              }
              triggerBatchLoad();
            }
          }
        });
      }, 150);
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    console.log('📜 已添加滚动事件监听器');

    return () => {
      container.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimer);
      console.log('📜 已移除滚动事件监听器');
    };
  }, [messages, triggerBatchLoad, loadMoreMessages, hasMore, loadingMore, loadingMessages]);

  // Tab切换的实际执行函数
  const doWxTabChange = async (wxid: string) => {
    // 立即更新 activeWxTab，确保 Tab 视觉状态立即响应
    setActiveWxTab(wxid);

    // 取消之前的请求
    if (loadingContacts) {
      cancelLoadContacts();
    }
    if (loadingChatrooms) {
      cancelLoadChatrooms();
    }

    // 如果正在刷新，不允许切换
    if (refreshingTabs.has(wxid)) {
      return;
    }

    // 生成新的请求序列号
    requestSequenceRef.current += 1;
    const currentSequence = requestSequenceRef.current;

    // 立即设置禁用状态（在调用接口之前）
    setRefreshingTabs(prev => new Set(prev).add(wxid));

    // 立即清空旧的联系人和群组数据
    setAllContactsData([]);
    setContactsPage(1);
    setContactsHasMore(true);
    mutateChatrooms(undefined);
    const wxUser = wxUsers?.find(wu => wu.wxid === wxid);
    if (wxUser && selectedUser) {
      setSelectedWxUser(wxUser);
      setSelectedContact(null);
      setSelectedChatroom(null);
      setMessages([]);

      try {
        // 并行请求两个接口，等待都完成
        await Promise.all([
          loadContacts(selectedUser.id as number, wxUser.wxid, contactsKeyword),
          loadChatrooms(selectedUser.id as number, wxUser.wxid)
        ]);

        // 检查请求序列号，只有最新的请求才处理结果
        if (currentSequence !== requestSequenceRef.current) {
          console.log('忽略过期的请求结果');
          return;
        }
      } catch (error) {
        // 如果是取消错误，不显示错误信息
        if (error && typeof error === 'object' && 'name' in error && error.name === 'CanceledError') {
          console.log('请求已被取消');
          return;
        }
        console.error('切换Tab加载数据失败:', error);
      } finally {
        // 只有当前序列号匹配时才移除刷新标记
        if (currentSequence === requestSequenceRef.current) {
          setRefreshingTabs(prev => {
            const next = new Set(prev);
            next.delete(wxid);
            return next;
          });
        }
      }
    } else {
      // 如果没有找到用户，也要移除禁用状态
      setRefreshingTabs(prev => {
        const next = new Set(prev);
        next.delete(wxid);
        return next;
      });
    }
  };

  // Tab切换防抖函数（300ms）
  const { run: handleWxTabChange } = useDebounceFn(doWxTabChange, {
    wait: 300,
  });

  // 刷新Tab数据的实际执行函数
  const doRefreshTabData = async (e: React.MouseEvent, wxid: string) => {
    e.stopPropagation(); // 阻止事件冒泡，避免触发Tab切换

    // 立即更新 activeWxTab，确保 Tab 视觉状态立即响应
    setActiveWxTab(wxid);

    // 取消之前的请求
    if (loadingContacts) {
      cancelLoadContacts();
    }
    if (loadingChatrooms) {
      cancelLoadChatrooms();
    }

    // 如果正在刷新，则不允许再次点击
    if (refreshingTabs.has(wxid)) {
      return;
    }

    if (selectedUser) {
      // 更新选中的微信用户
      const wxUser = wxUsers?.find(wu => wu.wxid === wxid);
      if (wxUser) {
        setSelectedWxUser(wxUser);
      }

      // 生成新的请求序列号
      requestSequenceRef.current += 1;
      const currentSequence = requestSequenceRef.current;

      // 标记为正在刷新
      setRefreshingTabs(prev => new Set(prev).add(wxid));

      try {
        // 并行请求两个接口
        await Promise.all([
          loadContacts(selectedUser.id as number, wxid, contactsKeyword),
          loadChatrooms(selectedUser.id as number, wxid)
        ]);

        // 检查请求序列号
        if (currentSequence !== requestSequenceRef.current) {
          console.log('忽略过期的刷新请求结果');
          return;
        }

        // message.success('刷新成功');
      } catch (error) {
        // 如果是取消错误，不显示错误信息
        if (error && typeof error === 'object' && 'name' in error && error.name === 'CanceledError') {
          console.log('刷新请求已被取消');
          return;
        }
        message.error('刷新失败');
        console.error('刷新Tab数据失败:', error);
      } finally {
        // 只有当前序列号匹配时才移除刷新标记
        if (currentSequence === requestSequenceRef.current) {
          setRefreshingTabs(prev => {
            const next = new Set(prev);
            next.delete(wxid);
            return next;
          });
        }
      }
    }
  };

  // 刷新Tab数据防抖函数（300ms）
  const { run: handleRefreshTabData } = useDebounceFn(doRefreshTabData, {
    wait: 300,
  });

  // 刷新当前聊天的消息
  const handleRefreshMessages = () => {
    if (selectedUser && selectedWxUser) {
      message.loading('刷新中...', 0.5);
      // 重置消息列表
      setMessages([]);
      setHasMore(true);
      isInitialLoadRef.current = true; // 重置初始加载标记
      if (selectedContact) {
        loadMessages(
          selectedUser.id as number,
          selectedWxUser.wxid,
          selectedContact.username,
        );
      } else if (selectedChatroom) {
        loadMessages(
          selectedUser.id as number,
          selectedWxUser.wxid,
          selectedChatroom.chatroomname,
        );
      }
    }
  };

  // 下发删除指定 talker 的全部聊天记录
  const handleDeleteMessagesByTalker = async () => {
    if (!selectedUser || !selectedWxUser) return;
    const talker = selectedContact?.username || selectedChatroom?.chatroomname;
    if (!talker) return;
    try {
      await systemController.sendWxDbDeleteMessagesByTalker({
        userId: Number(selectedUser.id),
        wxid: selectedWxUser.wxid,
        talker,
      });
      message.success('已下发删除指令，客户端执行后即生效');
    } catch (err: any) {
      message.error(err?.message || '下发失败');
    }
  };

  // 下发清空整个微信号的全部消息
  const handleDeleteAllMessages = async () => {
    if (!selectedUser || !selectedWxUser) return;
    try {
      await systemController.sendWxDbDeleteAllMessages({
        userId: Number(selectedUser.id),
        wxid: selectedWxUser.wxid,
      });
      message.success('已下发清空指令，客户端执行后即生效');
    } catch (err: any) {
      message.error(err?.message || '下发失败');
    }
  };

  // 下发按 msgId 批量删除
  const handleBatchDeleteByIds = async () => {
    if (!selectedUser || !selectedWxUser) return;
    const ids = batchDeleteMsgIds
      .split(/[\s,，]+/)
      .map((s) => s.trim())
      .filter((s) => s.length > 0)
      .map((s) => Number(s))
      .filter((n) => Number.isInteger(n) && n > 0);
    if (ids.length === 0) {
      message.warning('请输入至少一个有效的 msgId');
      return;
    }
    setBatchDeleteSubmitting(true);
    try {
      await systemController.sendWxDbDeleteMessagesByIds({
        userId: Number(selectedUser.id),
        wxid: selectedWxUser.wxid,
        msgIds: ids,
      });
      message.success(`已下发删除 ${ids.length} 条消息的指令`);
      setBatchDeleteModalOpen(false);
      setBatchDeleteMsgIds('');
    } catch (err: any) {
      message.error(err?.message || '下发失败');
    } finally {
      setBatchDeleteSubmitting(false);
    }
  };

  const handleContactClick = (contact: WechatContactDTO) => {
    setSelectedContact(contact);
    setSelectedChatroom(null);
    // 重置消息列表
    setMessages([]);
    setHasMore(true);
    isInitialLoadRef.current = true; // 重置初始加载标记
    if (selectedUser && selectedWxUser) {
      loadMessages(
        selectedUser.id as number,
        selectedWxUser.wxid,
        contact.username,
      );
    }
  };

  const handleChatroomClick = (chatroom: WechatChatroomDTO) => {
    setSelectedChatroom(chatroom);
    setSelectedContact(null);
    // 重置消息列表
    setMessages([]);
    setHasMore(true);
    isInitialLoadRef.current = true; // 重置初始加载标记
    if (selectedUser && selectedWxUser) {
      loadMessages(
        selectedUser.id as number,
        selectedWxUser.wxid,
        chatroom.chatroomname,
      );
    }
  };

  const parseLocationContent = (content: string) => {
    try {
      const xMatch = content.match(/x="([^"]+)"/);
      const yMatch = content.match(/y="([^"]+)"/);
      const labelMatch = content.match(/label="([^"]+)"/);
      const poinameMatch = content.match(/poiname="([^"]+)"/);
      
      return {
        latitude: xMatch ? xMatch[1] : '',
        longitude: yMatch ? yMatch[1] : '',
        label: labelMatch ? labelMatch[1] : '',
        poiname: poinameMatch ? poinameMatch[1] : '',
      };
    } catch {
      return null;
    }
  };

  // 解析聊天记录转发消息
  const parseChatRecordMessage = (xmlString: string) => {
    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlString, 'text/xml');
      
      const recorditem = xmlDoc.querySelector('recorditem');
      if (!recorditem) return null;

      const cdataContent = recorditem.textContent || '';
      const recordDoc = parser.parseFromString(cdataContent, 'text/xml');
      
      const recordinfo = recordDoc.querySelector('recordinfo');
      if (!recordinfo) return null;

      const title = recordinfo.querySelector('title')?.textContent;
      const desc = recordinfo.querySelector('desc')?.textContent;
      const isChatRoom = recordinfo.querySelector('isChatRoom')?.textContent === '1';
      
      const dataitems = recordDoc.querySelectorAll('dataitem');
      const records = Array.from(dataitems).map(item => ({
        datatype: item.getAttribute('datatype'),
        content: item.querySelector('datadesc')?.textContent?.replace(/&#x20;/g, ' ') || '',
        sourcename: item.querySelector('sourcename')?.textContent || '',
        sourceheadurl: item.querySelector('sourceheadurl')?.textContent || '',
        sourcetime: item.querySelector('sourcetime')?.textContent || '',
      }));

      return {
        title: title || '聊天记录',
        desc: desc?.replace(/&#x20;/g, ' ').replace(/&#x0A;/g, '\n') || '',
        isChatRoom,
        records,
        count: records.length,
      };
    } catch (error) {
      console.error('解析聊天记录失败:', error);
      return null;
    }
  };

  // 解析 XML 消息
  const parseXmlMessage = (xmlString: string) => {
    try {
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlString, 'text/xml');
      
      const parserError = xmlDoc.querySelector('parsererror');
      if (parserError) return null;
      
      const appmsg = xmlDoc.querySelector('appmsg');
      if (!appmsg) return null;

      const type = appmsg.querySelector('type')?.textContent;
      const title = appmsg.querySelector('title')?.textContent;
      const des = appmsg.querySelector('des')?.textContent;
      const url = appmsg.querySelector('url')?.textContent;
      
      // 聊天记录转发 (type 19)
      if (type === '19') {
        const recordData = parseChatRecordMessage(xmlString);
        return {
          type: 19,
          title: recordData?.title || title,
          des: recordData?.desc || des,
          url,
          chatRecord: recordData,
        };
      }
      
      const appattach = appmsg.querySelector('appattach');
      if (appattach) {
        const totallen = appattach.querySelector('totallen')?.textContent;
        const fileext = appattach.querySelector('fileext')?.textContent;
        return {
          type: parseInt(type || '0'),
          title,
          des,
          url,
          fileSize: totallen ? parseInt(totallen) : 0,
          fileExt: fileext,
        };
      }

      return {
        type: parseInt(type || '0'),
        title,
        des,
        url,
        fileSize: 0,
        fileExt: undefined,
      };
    } catch (error) {
      return null;
    }
  };

  // 从缓存中获取文件URL（同步）
  const getCachedFileUrl = (msg: WechatMessageDTO): string | null => {
    if (!selectedUser) return null;
    
    const msgSvrIdStr = msg.msgSvrId.toString();
    const cachedFile = fileCache.current.get(msgSvrIdStr);
    
    if (cachedFile && cachedFile.key) {
      // 图片类型（type 3）使用 OSS URL
      if (msg.type === 3) {
        return getImageUrl(cachedFile.key);
      }
      // 其他类型使用下载 URL
      if (cachedFile.userFile) {
        return getFileDownloadUrl(selectedUser.id as number, cachedFile.key);
      }
    }
    
    return null;
  };

  // 获取文件 URL（从缓存或加载）
  const getFileUrl = async (msg: WechatMessageDTO): Promise<string | null> => {
    if (!company?.id || !selectedUser || !selectedWxUser) return null;

    const msgSvrIdStr = msg.msgSvrId.toString();

    // 先从缓存中查找
    const cachedFile = fileCache.current.get(msgSvrIdStr);
    if (cachedFile && cachedFile.key) {
      // 图片类型（type 3）使用 OSS URL
      if (msg.type === 3) {
        return getImageUrl(cachedFile.key);
      }
      // 其他类型使用下载 URL
      if (cachedFile.userFile) {
        return getFileDownloadUrl(selectedUser.id as number, cachedFile.key);
        }
      }

    // 如果不在缓存中且没在加载中，添加到待加载队列
    if (!loadingFiles.current.has(msgSvrIdStr)) {
      if (!pendingFiles.current.includes(msgSvrIdStr)) {
        pendingFiles.current.push(msgSvrIdStr);
      }
      
      // 触发批量加载
      triggerBatchLoad();
    }

    return null;
  };

  // 处理图片点击
  const handleImageClick = async (msg: WechatMessageDTO) => {
    const url = await getFileUrl(msg);
    if (url) {
      // 可以使用 Image 预览组件
      window.open(url, '_blank');
    } else {
      message.warning('图片加载失败');
    }
  };

  // 处理视频点击
  const handleVideoClick = async (msg: WechatMessageDTO) => {
    const url = await getFileUrl(msg);
    if (url) {
      // 打开视频播放器
      setVideoPlayerUrl(url);
      setVideoPlayerVisible(true);
    } else {
      message.warning('视频加载失败');
    }
  };

  // 处理语音点击
  const handleVoiceClick = async (msg: WechatMessageDTO) => {
    const url = await getFileUrl(msg);
    if (url) {
      // 播放音频
      const audio = new Audio(url);
      audio.play().catch(() => {
        message.error('语音播放失败');
      });
    } else {
      message.warning('语音加载失败');
    }
  };

  // 处理文件下载
  const handleFileDownload = async (msg: WechatMessageDTO) => {
    const url = await getFileUrl(msg);
    if (url) {
      window.open(url, '_blank');
    } else {
      message.warning('文件不存在或已过期');
    }
  };

  // 视频播放器状态
  const [videoPlayerVisible, setVideoPlayerVisible] = useState(false);
  const [videoPlayerUrl, setVideoPlayerUrl] = useState('');

  // 聊天记录查看器状态
  const [chatRecordVisible, setChatRecordVisible] = useState(false);
  const [chatRecordData, setChatRecordData] = useState<any>(null);

  // 接龙消息展开状态
  const [expandedSolitaires, setExpandedSolitaires] = useState<Set<number>>(new Set());

  // 解析群聊消息内容
  // 群聊消息格式：username:\n消息内容
  const parseGroupMessage = (content: string) => {
    if (!content) return { sender: null, senderUsername: null, message: content, senderContact: null };

    // 检查是否包含 : 和 换行符
    const colonIndex = content.indexOf(':');
    const newlineIndex = content.indexOf('\n');

    if (colonIndex > 0 && newlineIndex > colonIndex) {
      const senderUsername = content.substring(0, colonIndex).trim();
      const messageContent = content.substring(newlineIndex + 1).trim();

      // 从联系人列表中查找发送者信息（使用完整的 allContactsData，包括被过滤的联系人）
      const senderContact = allContactsData?.find(c => c.username === senderUsername);
      // 如果有 alias，显示为 nickname（alias），否则只显示 nickname
      let senderName = senderUsername;
      if (senderContact) {
        if (senderContact.alias && senderContact.nickname) {
          senderName = `${senderContact.nickname}（${senderContact.alias}）`;
        } else {
          senderName = senderContact.nickname || senderContact.alias || senderUsername;
        }
      }

      return {
        sender: senderName,
        senderUsername: senderUsername,
        message: messageContent,
        senderContact: senderContact, // 返回完整的联系人对象
      };
    }

    return { sender: null, senderUsername: null, message: content, senderContact: null };
  };

  // 获取群聊消息发送者名称
  const getGroupMessageSender = (msg: WechatMessageDTO) => {
    if (!selectedChatroom || msg.isSend === 1) {
      return null;
    }
    
    const { sender } = parseGroupMessage(msg.content);
    return sender;
  };

  // 格式化联系人显示名称：如果有 alias，显示为 nickname（alias）
  const formatContactName = (contact: WechatContactDTO | null | undefined) => {
    if (!contact) return '';
    
    const nickname = contact.nickname || contact.username;
    const alias = contact.alias;
    
    // 如果有 alias，显示为 nickname（alias）
    if (alias) {
      return `${nickname}（${alias}）`;
    }
    
    // 没有 alias，只显示 nickname
    return nickname;
  };

  // 渲染群聊头像 - 拼接成员头像
  const renderChatroomAvatar = useCallback((item: any) => {
    if (item.type !== 'chatroom') {
      // 普通联系人 - 使用简单的 img 标签
      return item.avatar ? (
        <img
          src={item.avatar}
          alt="avatar"
          referrerPolicy="no-referrer"
          style={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            objectFit: 'cover'
          }}
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.style.display = 'none';
            target.parentElement!.innerHTML = '<span class="ant-avatar ant-avatar-icon"><span role="img" aria-label="user" class="anticon anticon-user"><svg viewBox="64 64 896 896" focusable="false" data-icon="user" width="1em" height="1em" fill="currentColor" aria-hidden="true"><path d="M858.5 763.6a374 374 0 00-80.6-119.5 375.63 375.63 0 00-119.5-80.6c-.4-.2-.8-.3-1.2-.5C719.5 518 760 444.7 760 362c0-137-111-248-248-248S264 225 264 362c0 82.7 40.5 156 102.8 201.1-.4.2-.8.3-1.2.5-44.8 18.9-85 46-119.5 80.6a375.63 375.63 0 00-80.6 119.5A371.7 371.7 0 00136 901.8a8 8 0 008 8.2h60c4.4 0 7.9-3.5 8-7.8 2-77.2 33-149.5 87.8-204.3 56.7-56.7 132-87.9 212.2-87.9s155.5 31.2 212.2 87.9C779 752.7 810 825 812 902.2c.1 4.4 3.6 7.8 8 7.8h60a8 8 0 008-8.2c-1-47.8-10.9-94.3-29.5-138.2zM512 534c-45.9 0-89.1-17.9-121.6-50.4S340 407.9 340 362c0-45.9 17.9-89.1 50.4-121.6S466.1 190 512 190s89.1 17.9 121.6 50.4S684 316.1 684 362c0 45.9-17.9 89.1-50.4 121.6S557.9 534 512 534z"></path></svg></span></span>';
          }}
        />
      ) : (
        <Avatar icon={<UserOutlined />} />
      );
    }

    // 群聊头像 - 拼接成员头像（4宫格、6宫格、9宫格）
    const memberIds = item.memberlist?.split(';').filter((id: string) => id.trim()) || [];
    const memberAvatars = memberIds.slice(0, 9).map((memberId: string) => {
      const contact = allContactsData?.find(c => c.username === memberId.trim());
      return contact?.avatar;
    }).filter(Boolean); // 过滤掉空头像

    // 如果没有成员头像，显示默认群聊图标
    if (memberAvatars.length === 0) {
      return (
        <Avatar
          icon={<TeamOutlined />}
          style={{
            backgroundColor: '#95de64',
            width: 40,
            height: 40,
          }}
        />
      );
    }

    // 根据成员数量决定布局：1-4个用2x2，5-6个用2x3，7-9个用3x3
    let gridCols = 2;
    let gridRows = 2;
    let displayCount = 4;

    if (memberAvatars.length >= 7) {
      gridCols = 3;
      gridRows = 3;
      displayCount = 9;
    } else if (memberAvatars.length >= 5) {
      gridCols = 3;
      gridRows = 2;
      displayCount = 6;
    }

    const displayAvatars = memberAvatars.slice(0, displayCount);
    const containerSize = 40;
    const gap = 1;

    return (
      <div
        style={{
          width: containerSize,
          height: containerSize,
          display: 'grid',
          gridTemplateColumns: `repeat(${gridCols}, 1fr)`,
          gridTemplateRows: `repeat(${gridRows}, 1fr)`,
          gap: `${gap}px`,
          background: '#e8e8e8',
          borderRadius: '4px',
          overflow: 'hidden',
        }}
      >
        {displayAvatars.map((avatar: string, index: number) => (
          <div
            key={index}
            style={{
              width: '100%',
              height: '100%',
              background: avatar ? `url(${avatar})` : '#bfbfbf',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {!avatar && (
              <UserOutlined style={{ fontSize: 8, color: '#fff' }} />
            )}
          </div>
        ))}
      </div>
    );
  }, [allContactsData]); // 依赖 allContactsData 以获取成员头像

  // 注册文件消息元素到 Observer
  const registerFileMessage = useCallback((element: HTMLDivElement | null, msgSvrId: string, msgType?: number) => {
    if (!element) {
      console.warn('⚠️ 注册文件消息失败：元素为空', msgSvrId);
      return;
    }
    
    if (!observerRef.current) {
      console.warn('⚠️ 注册文件消息失败：Observer 未初始化', msgSvrId);
      // Observer 可能还没初始化，延迟注册
      setTimeout(() => {
        if (observerRef.current && element) {
          fileMessageRefs.current.set(msgSvrId, element);
          observerRef.current.observe(element);
          console.log('✅ 延迟注册文件消息成功:', msgSvrId, '类型:', msgType);
        }
      }, 100);
      return;
    }
    
    // 保存元素引用
    fileMessageRefs.current.set(msgSvrId, element);
    
    // 开始观察
    observerRef.current.observe(element);
    console.log('✅ 注册文件消息到 Observer:', msgSvrId, '类型:', msgType);
  }, []);

  const renderMessageContent = (msg: WechatMessageDTO) => {
    // 群聊消息需要解析出真实内容
    let displayContent = msg.content;
    
    if (selectedChatroom && msg.isSend === 0 && msg.type === 1) {
      const { message } = parseGroupMessage(msg.content);
      displayContent = message;
    }
    
    // 根据消息类型渲染不同内容
    switch (msg.type) {
      case 1: // 文本消息
        return <Text>{displayContent}</Text>;
      case 3: // 图片消息
        const msgSvrIdStr = msg.msgSvrId.toString();
        const cachedFile = fileCache.current.get(msgSvrIdStr);
        const imageKey = cachedFile?.key || '';
        
        return (
          <div 
            ref={(el) => registerFileMessage(el, msgSvrIdStr, msg.type)}
            data-msg-svr-id={msgSvrIdStr}
            data-msg-type={msg.type}
            className="image-message-content"
            style={{ cursor: imageKey ? 'zoom-in' : 'pointer' }}
          >
            {imageKey ? (
              <WechatImagePreview
                ossKey={imageKey}
                width={200}
                process="image/resize,m_fill,w_400,h_400,image/format,webp"
                preview={{
                  mask: '查看大图',
                }}
                style={{ maxWidth: 200, borderRadius: 4 }}
              />
            ) : (
              <div 
                className="image-placeholder"
                onClick={() => handleImageClick(msg)}
              >
                <FileImageOutlined style={{ fontSize: 40, color: '#999' }} />
                <Text type="secondary" style={{ marginTop: 8 }}>
                  {loadingFiles.current.has(msgSvrIdStr) ? '加载中...' : '点击加载图片'}
                </Text>
              </div>
            )}
          </div>
        );
      case 34: // 语音消息
        const voiceMsgSvrId = msg.msgSvrId.toString();
        const cachedVoiceFile = fileCache.current.get(voiceMsgSvrId);
        const voiceKey = cachedVoiceFile?.key || '';
        
        return (
          <div 
            ref={(el) => registerFileMessage(el, voiceMsgSvrId, msg.type)}
            data-msg-svr-id={voiceMsgSvrId}
            data-msg-type={msg.type}
            className="voice-message-content"
          >
            {voiceKey ? (
              <WechatVoicePreview
                ossKey={voiceKey}
                style={{ width: '100%' }}
              />
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }} onClick={() => handleVoiceClick(msg)}>
            <SoundOutlined style={{ fontSize: 20 }} />
                <div className="voice-duration">
                  {loadingFiles.current.has(voiceMsgSvrId) ? '加载中...' : '1"'}
                </div>
              </div>
            )}
          </div>
        );
      case 43: // 视频消息
        const cachedVideoThumbnail = getCachedFileUrl(msg);
        const videoThumbnail = msg.imgPath || cachedVideoThumbnail;
        const videoMsgSvrId = msg.msgSvrId.toString();
        
        return (
          <div 
            ref={(el) => registerFileMessage(el, videoMsgSvrId, msg.type)}
            data-msg-svr-id={videoMsgSvrId}
            data-msg-type={msg.type}
            className="video-message-content"
            style={{ cursor: 'pointer' }}
            onClick={() => handleVideoClick(msg)}
          >
            {videoThumbnail ? (
              <div className="video-thumbnail">
                <Image
                  src={videoThumbnail}
                  alt="视频"
                  style={{ width: 200, height: 150, objectFit: 'cover' }}
                  preview={false}
                  referrerPolicy="no-referrer"
                />
                <div className="video-play-icon">
                  <VideoCameraOutlined style={{ fontSize: 40, color: '#fff' }} />
                </div>
              </div>
            ) : (
              <div className="video-placeholder">
                <VideoCameraOutlined style={{ fontSize: 40 }} />
                <Text type="secondary">
                  {loadingFiles.current.has(videoMsgSvrId) ? '加载中...' : '视频'}
                </Text>
              </div>
            )}
          </div>
        );
      case 42: // 名片消息
        // 解析名片内容，格式：<msg username="..." nickname="..." alias="..." .../>
        let cardUsername = '';
        let cardNickname = '';
        let cardAlias = '';
        let cardAvatar = '';
        let cardSex = '';
        let cardRegion = '';

        if (msg.content) {
          try {
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(msg.content, 'text/xml');
            const msgNode = xmlDoc.querySelector('msg');

            if (msgNode) {
              // 获取名片属性
              cardUsername = msgNode.getAttribute('username') || '';
              const rawNickname = msgNode.getAttribute('nickname') || '';
              cardAlias = msgNode.getAttribute('alias') || '';
              const sex = msgNode.getAttribute('sex') || '';
              const province = msgNode.getAttribute('province') || '';
              const city = msgNode.getAttribute('city') || '';

              // 性别处理
              cardSex = sex === '1' ? '男' : sex === '2' ? '女' : '';

              // 地区处理
              if (province || city) {
                cardRegion = [province, city].filter(Boolean).join(' ');
              }

              // 从联系人列表中查找该用户的完整信息
              const cardContact = allContactsData?.find(c => c.username === cardUsername);
              if (cardContact) {
                // 使用联系人列表中的头像和信息
                cardAvatar = cardContact.avatar;
                // 如果联系人有备注，优先使用备注
                if (cardContact.alias) {
                  cardNickname = `${cardContact.nickname || rawNickname}（${cardContact.alias}）`;
                } else {
                  cardNickname = cardContact.nickname || rawNickname;
                }
              } else {
                // 使用名片中的信息
                if (cardAlias) {
                  cardNickname = `${rawNickname}（${cardAlias}）`;
                } else {
                  cardNickname = rawNickname;
                }
              }
            }
          } catch (error) {
            console.error('解析名片消息失败:', error);
            cardNickname = '未知用户';
          }
        }

        return (
          <div className="wechat-card-message">
            <div className="card-header">
              <UserOutlined style={{ fontSize: 12, marginRight: 4, color: '#07c160' }} />
              <Text strong style={{ fontSize: 11 }}>个人名片</Text>
            </div>
            <div className="card-content">
              <Avatar
                src={cardAvatar}
                icon={<UserOutlined />}
                size={40}
                style={{ flexShrink: 0 }}
                {...({ referrerPolicy: 'no-referrer' } as any)}
              />
              <div className="card-info">
                <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 2 }}>
                  {cardNickname || '未知用户'}
                </Text>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  {cardUsername && (
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {cardUsername}
                    </Text>
                  )}
                  {cardSex && (
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {cardSex}
                    </Text>
                  )}
                </div>
                {cardRegion && (
                  <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 2 }}>
                    <EnvironmentOutlined style={{ fontSize: 11, marginRight: 2 }} />
                    {cardRegion}
                  </Text>
                )}
              </div>
            </div>
          </div>
        );
      case 47: // 表情消息
        return (
          <Space>
            <Text type="secondary">[表情]</Text>
          </Space>
        );
      case 48: // 定位消息
        const locationInfo = parseLocationContent(msg.content);
        return (
          <div className="location-message-content">
            <div className="location-header">
              <EnvironmentOutlined style={{ fontSize: 16, marginRight: 6 }} />
              <Text strong>{locationInfo?.poiname || '位置信息'}</Text>
            </div>
            {locationInfo?.label && (
              <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
                {locationInfo.label}
              </Text>
            )}
            <div className="location-map-placeholder">
              <EnvironmentOutlined style={{ fontSize: 32, color: '#c8c9cc' }} />
            </div>
          </div>
         );
      case 50: // 语音通话消息（一对一聊天）
        // 解析 raw 字段获取 status
        let voiceCallText = '语音通话';
        try {
          if (msg.raw) {
            const rawData = JSON.parse(msg.raw);
            const status = rawData.status;
            if (status === 3) {
              voiceCallText = '语音通话';
            } else if (status === 6) {
              voiceCallText = '语音通话已取消';
            }
          }
        } catch (error) {
          console.error('解析语音通话消息失败:', error);
        }
        return (
          <Space>
            <SoundOutlined style={{ fontSize: 16 }} />
            <Text>{voiceCallText}</Text>
          </Space>
        );
      case 49: // 链接/文件消息
        const isXmlContent = msg.content && (msg.content.trim().startsWith('<?xml') || msg.content.trim().startsWith('<msg>'));
        if (!isXmlContent) {
          return (
            <Space>
              <Text type="secondary">[文件/链接]</Text>
            </Space>
          );
        }

        const xmlData = parseXmlMessage(msg.content);
        if (!xmlData) {
          return (
            <Space>
              <Text type="secondary">[文件/链接]</Text>
            </Space>
          );
        }

        // 聊天记录转发 (type 19)
        if (xmlData.type === 19 && xmlData.chatRecord) {
          return (
            <div 
              className="wechat-chatrecord-card"
              onClick={() => {
                setChatRecordData(xmlData.chatRecord);
                setChatRecordVisible(true);
              }}
            >
              <div className="chatrecord-header">
                <MessageOutlined style={{ fontSize: 16, marginRight: 6, color: '#07c160' }} />
                <Text strong>{xmlData.chatRecord.title}</Text>
              </div>
              <div className="chatrecord-preview">
                {xmlData.chatRecord.desc.split('\n').slice(0, 3).map((line: string, idx: number) => (
                  <div key={idx} className="chatrecord-line">
                    <Text type="secondary" ellipsis>{line}</Text>
                  </div>
                ))}
              </div>
              <div className="chatrecord-footer">
                <Text type="secondary" style={{ fontSize: 12 }}>
                  聊天记录
                </Text>
              </div>
            </div>
          );
        }

        // 文件消息 (type 6)
        if (xmlData.type === 6 && xmlData.fileSize) {
          const fileMsgSvrId = msg.msgSvrId.toString();
          return (
            <div 
              ref={(el) => registerFileMessage(el, fileMsgSvrId, msg.type)}
              data-msg-svr-id={fileMsgSvrId}
              data-msg-type={msg.type}
              className="wechat-file-card"
            >
              <div className="file-icon">
                <FileOutlined style={{ fontSize: 32, color: '#07c160' }} />
              </div>
              <div className="file-info">
                <div className="file-title">{xmlData.title || '文件'}</div>
                <div className="file-size">{formatFileSize(xmlData.fileSize)}</div>
              </div>
              <DownloadOutlined 
                className="file-download-icon"
                style={{ fontSize: 18, color: '#999', cursor: 'pointer' }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleFileDownload(msg);
                }}
              />
            </div>
          );
        }

        // 链接消息 (type 5)
        if (xmlData.type === 5) {
          // 解析来源信息
          let sourceName = '';
          try {
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(msg.content, 'text/xml');
            sourceName = xmlDoc.querySelector('sourcedisplayname')?.textContent || '';
          } catch (error) {
            console.error('解析链接来源失败:', error);
          }

          const handleLinkClick = () => {
            if (xmlData.url) {
              // 解码 HTML 实体（如 &amp; -> &）
              const decodedUrl = xmlData.url.replace(/&amp;/g, '&');
              window.open(decodedUrl, '_blank', 'noopener,noreferrer');
            }
          };

          return (
            <div
              className="wechat-link-card"
              onClick={handleLinkClick}
              style={{ cursor: xmlData.url ? 'pointer' : 'default' }}
            >
              <div className="link-content">
                <div className="link-title">{xmlData.title || '链接'}</div>
                {xmlData.des && (
                  <div className="link-desc">{xmlData.des}</div>
                )}
                {sourceName && (
                  <div className="link-source" style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                    {sourceName}
                  </div>
                )}
              </div>
              <LinkOutlined className="link-icon" />
            </div>
          );
        }

        // 其他类型
        return (
          <div className="wechat-link-card">
            <div className="link-content">
              <div className="link-title">{xmlData.title || '消息'}</div>
              {xmlData.des && (
                <div className="link-desc">{xmlData.des}</div>
              )}
            </div>
          </div>
        );
      case 419430449: // 转账消息
        // 先解析群聊消息，提取真实的 XML 内容
        let transferXmlContent = msg.content;
        if (selectedChatroom && msg.isSend === 0) {
          const { message } = parseGroupMessage(msg.content);
          transferXmlContent = message;
        }

        // 检查是否为 XML 格式
        const isXmlTransfer = transferXmlContent && (transferXmlContent.trim().startsWith('<?xml') || transferXmlContent.trim().startsWith('<msg>'));

        let transferAmount = '';
        let receiverUsername = '';
        let receiverNickname = '';
        let payerUsername = '';
        let payerNickname = '';
        let paysubtype = '';
        let isReceived = false;
        let payMemo = ''; // 转账说明

        if (isXmlTransfer) {
          try {
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(transferXmlContent, 'text/xml');
            const wcpayinfo = xmlDoc.querySelector('wcpayinfo');

            if (wcpayinfo) {
              // 获取转账金额
              transferAmount = wcpayinfo.querySelector('feedesc')?.textContent || '';
              // 获取收款人 username
              receiverUsername = wcpayinfo.querySelector('receiver_username')?.textContent || '';
              // 获取付款人 username
              payerUsername = wcpayinfo.querySelector('payer_username')?.textContent || '';
              // 获取支付子类型（paysubtype: 1=未收款，3=已收款）
              paysubtype = wcpayinfo.querySelector('paysubtype')?.textContent || '';
              // 获取转账说明
              payMemo = wcpayinfo.querySelector('pay_memo')?.textContent || '';

              // 判断是否已收款
              isReceived = paysubtype === '3';

              // 从联系人列表中查找收款人信息
              if (receiverUsername) {
                const receiverContact = allContactsData?.find(c => c.username === receiverUsername);
                if (receiverContact) {
                  if (receiverContact.alias && receiverContact.nickname) {
                    receiverNickname = `${receiverContact.nickname}（${receiverContact.alias}）`;
                  } else {
                    receiverNickname = receiverContact.nickname || receiverContact.alias || receiverUsername;
                  }
                } else {
                  receiverNickname = receiverUsername;
                }
              }

              // 从联系人列表中查找付款人信息
              if (payerUsername) {
                const payerContact = allContactsData?.find(c => c.username === payerUsername);
                if (payerContact) {
                  if (payerContact.alias && payerContact.nickname) {
                    payerNickname = `${payerContact.nickname}（${payerContact.alias}）`;
                  } else {
                    payerNickname = payerContact.nickname || payerContact.alias || payerUsername;
                  }
                } else {
                  // 如果没找到联系人，检查是否是当前微信用户
                  if (selectedWxUser && payerUsername === selectedWxUser.wxid) {
                    payerNickname = selectedWxUser.nickname || '我';
                  } else {
                    payerNickname = payerUsername;
                  }
                }
              }
            }
          } catch (error) {
            console.error('解析转账消息失败:', error);
          }
        }

        // 统一显示格式：谁转给谁 + 金额 + 转账说明 + 状态
        // 未收款：显示 "xxx 转给 xxx"
        // 已收款：显示 "xxx 转给 xxx 的" + 金额 + "已收款"

        return (
          <div className="wechat-transfer-wrapper">
            <div className="wechat-transfer-card">
              <div className="transfer-icon" style={{ fontSize: 14 }}>￥</div>
              <div className="transfer-content">
                <div className="transfer-title" style={{ fontSize: 10 }}>转账</div>
                {payerNickname && receiverNickname && (
                  <div className="transfer-info" style={{ fontSize: 10, color: '#fff', marginTop: 1 }}>
                    {payerNickname} 转给 {receiverNickname}{isReceived ? ' 的' : ''}
                  </div>
                )}
                {transferAmount && (
                  <div className="transfer-amount" style={{ fontSize: 12, color: '#fff', fontWeight: 'bold', marginTop: 1 }}>
                    {transferAmount}元
                  </div>
                )}
                {payMemo && (
                  <div className="transfer-memo" style={{ fontSize: 9, color: '#fff', marginTop: 1, opacity: 0.85 }}>
                    {payMemo}
                  </div>
                )}
                {isReceived && (
                  <div className="transfer-status" style={{ fontSize: 9, color: '#fff', marginTop: 1 }}>
                    已收款
                  </div>
                )}
                {!transferAmount && (!payerNickname || !receiverNickname) && (
                  <div className="transfer-tip" style={{ fontSize: 9 }}>请在手机上查看</div>
                )}
              </div>
            </div>
          </div>
        );
      case 436207665: // 红包消息
        return (
          <div className="wechat-redpacket-wrapper">
            <div className="wechat-redpacket-card">
              <div className="redpacket-main">
                <div className="redpacket-icon-box">
                  <GiftOutlined className="redpacket-icon" />
            </div>
                <div className="redpacket-content">
                  <div className="redpacket-wish">恭喜发财，大吉大利</div>
                </div>
              </div>
              <div className="redpacket-footer">
                <span>微信红包</span>
              </div>
            </div>
          </div>
        );
      case 1040187441: // 音乐分享消息
        // 先解析群聊消息，提取真实的 XML 内容
        let musicXmlContent = msg.content;
        if (selectedChatroom && msg.isSend === 0) {
          const { message } = parseGroupMessage(msg.content);
          musicXmlContent = message;
        }

        // 检查是否为 XML 格式
        const isXmlMusic = musicXmlContent && musicXmlContent.trim().startsWith('<?xml');
        if (!isXmlMusic) {
          return (
            <Space>
              <Text type="secondary">[音乐分享]</Text>
            </Space>
          );
        }

        const musicData = parseXmlMessage(musicXmlContent);
        if (!musicData) {
          return (
            <Space>
              <Text type="secondary">[音乐分享]</Text>
            </Space>
          );
        }

        return (
          <div className="wechat-link-card">
            <div className="link-content">
              <div className="link-title">
                <SoundOutlined style={{ marginRight: 6, color: '#07c160' }} />
                {musicData.title || '音乐分享'}
              </div>
              {musicData.des && (
                <div className="link-desc">{musicData.des}</div>
              )}
            </div>
          </div>
        );
      case 452984881: // 发票分享消息
        // 先解析群聊消息，提取真实的 XML 内容
        let invoiceXmlContent = msg.content;
        if (selectedChatroom && msg.isSend === 0) {
          const { message } = parseGroupMessage(msg.content);
          invoiceXmlContent = message;
        }

        // 检查是否为 XML 格式（发票消息可能以 <msg> 开头，不一定有 <?xml）
        const isXmlInvoice = invoiceXmlContent && (invoiceXmlContent.trim().startsWith('<?xml') || invoiceXmlContent.trim().startsWith('<msg>'));
        if (!isXmlInvoice) {
          return (
            <Space>
              <Text type="secondary">[发票]</Text>
            </Space>
          );
        }

        try {
          const parser = new DOMParser();
          const xmlDoc = parser.parseFromString(invoiceXmlContent, 'text/xml');

          const appmsg = xmlDoc.querySelector('appmsg');
          if (!appmsg) {
            return (
              <Space>
                <Text type="secondary">[发票]</Text>
              </Space>
            );
          }

          const title = appmsg.querySelector('title')?.textContent || '发票';
          const des = appmsg.querySelector('des')?.textContent;
          const carditem = appmsg.querySelector('carditem');
          const brandName = carditem?.querySelector('brand_name')?.textContent;

          return (
            <div className="wechat-link-card">
              <div className="link-content">
                <div className="link-title">
                  <FileOutlined style={{ marginRight: 6, color: '#cc463d' }} />
                  {title}
                </div>
                {des && (
                  <div className="link-desc">{des}</div>
                )}
                {brandName && (
                  <div className="link-desc" style={{ fontSize: 12, color: '#999' }}>
                    {brandName}
                  </div>
                )}
              </div>
            </div>
          );
        } catch (error) {
          console.error('解析发票消息失败:', error);
          return (
            <Space>
              <Text type="secondary">[发票]</Text>
            </Space>
          );
        }
      case 1090519089: // 文件分享消息
        // 先解析群聊消息，提取真实的 XML 内容
        let fileXmlContent = msg.content;
        if (selectedChatroom && msg.isSend === 0) {
          const { message } = parseGroupMessage(msg.content);
          fileXmlContent = message;
        }

        // 检查是否为 XML 格式
        const isXmlFile = fileXmlContent && (fileXmlContent.trim().startsWith('<?xml') || fileXmlContent.trim().startsWith('<msg>'));
        if (!isXmlFile) {
          return (
            <Space>
              <Text type="secondary">[文件]</Text>
            </Space>
          );
        }

        try {
          const parser = new DOMParser();
          const xmlDoc = parser.parseFromString(fileXmlContent, 'text/xml');

          const appmsg = xmlDoc.querySelector('appmsg');
          if (!appmsg) {
            return (
              <Space>
                <Text type="secondary">[文件]</Text>
              </Space>
            );
          }

          const fileTitle = appmsg.querySelector('title')?.textContent || '文件';
          const appattach = appmsg.querySelector('appattach');
          const totallen = appattach?.querySelector('totallen')?.textContent;
          const fileSize = totallen ? parseInt(totallen) : 0;

          return (
            <div className="wechat-file-card">
              <div className="file-icon">
                <FileOutlined style={{ fontSize: 32, color: '#07c160' }} />
              </div>
              <div className="file-info">
                <div className="file-title">{fileTitle}</div>
                <div className="file-size">{formatFileSize(fileSize)}</div>
              </div>
            </div>
          );
        } catch (error) {
          console.error('解析文件消息失败:', error);
          return (
            <Space>
              <Text type="secondary">[文件]</Text>
            </Space>
          );
        }
      case 822083633: // 引用消息
        // 先解析群聊消息，提取真实的 XML 内容
        let quoteXmlContent = msg.content;
        if (selectedChatroom && msg.isSend === 0) {
          const { message } = parseGroupMessage(msg.content);
          quoteXmlContent = message;
        }

        // 检查是否为 XML 格式
        const isXmlQuote = quoteXmlContent && (quoteXmlContent.trim().startsWith('<?xml') || quoteXmlContent.trim().startsWith('<msg>'));
        if (!isXmlQuote) {
          return (
            <Space>
              <Text type="secondary">[引用消息]</Text>
            </Space>
          );
        }

        try {
          const parser = new DOMParser();
          const xmlDoc = parser.parseFromString(quoteXmlContent, 'text/xml');

          const appmsg = xmlDoc.querySelector('appmsg');
          if (!appmsg) {
            return (
              <Space>
                <Text type="secondary">[引用消息]</Text>
              </Space>
            );
          }

          // 获取回复内容
          const replyText = appmsg.querySelector('title')?.textContent || '';

          // 获取引用的原消息信息
          const refermsg = appmsg.querySelector('refermsg');
          let quotedContent = '';
          let quotedDisplayName = '';
          let quotedTime = '';

          if (refermsg) {
            const displayname = refermsg.querySelector('displayname')?.textContent || '';
            const content = refermsg.querySelector('content')?.textContent || '';
            const createtime = refermsg.querySelector('createtime')?.textContent || '';

            // 格式化时间
            if (createtime) {
              const timestamp = parseInt(createtime) * 1000; // 转换为毫秒
              quotedTime = dayjs(timestamp).format('YYYY-MM-DD HH:mm:ss');
            }

            // 查找引用者的昵称
            if (displayname) {
              const quotedContact = allContactsData?.find(c => c.username === displayname);
              if (quotedContact) {
                if (quotedContact.alias && quotedContact.nickname) {
                  quotedDisplayName = `${quotedContact.nickname}（${quotedContact.alias}）`;
                } else {
                  quotedDisplayName = quotedContact.nickname || quotedContact.alias || displayname;
                }
              } else {
                quotedDisplayName = displayname;
              }
            }

            // 解析引用的消息内容
            if (content) {
              try {
                const contentParser = new DOMParser();
                const contentDoc = contentParser.parseFromString(content, 'text/xml');
                const contentAppmsg = contentDoc.querySelector('appmsg');

                if (contentAppmsg) {
                  const contentType = contentAppmsg.querySelector('type')?.textContent;

                  // 根据不同类型显示不同的引用内容
                  if (contentType === '2001') {
                    // 红包消息
                    quotedContent = '[红包]';
                  } else {
                    const contentTitle = contentAppmsg.querySelector('title')?.textContent;
                    const contentDes = contentAppmsg.querySelector('des')?.textContent;
                    quotedContent = contentTitle || contentDes || '[消息]';
                  }
                } else {
                  quotedContent = content.substring(0, 50) + (content.length > 50 ? '...' : '');
                }
              } catch (e) {
                quotedContent = content.substring(0, 50) + (content.length > 50 ? '...' : '');
              }
            }
          }

          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {/* 引用的原消息 */}
              {quotedContent && (
                <div
                  style={{
                    padding: '8px 12px',
                    borderLeft: '3px solid #07c160',
                    backgroundColor: 'rgba(0, 0, 0, 0.05)',
                    borderRadius: 4,
                    fontSize: 12,
                  }}
                >
                  {quotedDisplayName && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                      <div style={{ fontWeight: 'bold', opacity: 0.8 }}>
                        {quotedDisplayName}
                      </div>
                      {quotedTime && (
                        <div style={{ fontSize: 11, opacity: 0.6 }}>
                          {quotedTime}
                        </div>
                      )}
                    </div>
                  )}
                  <div style={{ opacity: 0.7 }}>{quotedContent}</div>
                </div>
              )}
              {/* 回复内容 */}
              {replyText && (
                <div>
                  <Text>{replyText}</Text>
                </div>
              )}
            </div>
          );
        } catch (error) {
          console.error('解析引用消息失败:', error);
          return (
            <Space>
              <Text type="secondary">[引用消息]</Text>
            </Space>
          );
        }
      case 805306417: // 接龙消息
        // 先解析群聊消息，提取真实的 XML 内容
        let solitaireXmlContent = msg.content;
        if (selectedChatroom && msg.isSend === 0) {
          const { message } = parseGroupMessage(msg.content);
          solitaireXmlContent = message;
        }

        // 检查是否为 XML 格式
        const isXmlSolitaire = solitaireXmlContent && (solitaireXmlContent.trim().startsWith('<?xml') || solitaireXmlContent.trim().startsWith('<msg>'));
        if (!isXmlSolitaire) {
          return (
            <Space>
              <Text type="secondary">[接龙]</Text>
            </Space>
          );
        }

        try {
          const parser = new DOMParser();
          const xmlDoc = parser.parseFromString(solitaireXmlContent, 'text/xml');

          const appmsg = xmlDoc.querySelector('appmsg');
          if (!appmsg) {
            return (
              <Space>
                <Text type="secondary">[接龙]</Text>
              </Space>
            );
          }

          const solitaireTitle = appmsg.querySelector('title')?.textContent || '接龙';
          // textContent 已经自动将 HTML 实体转换为实际字符
          // &#x0A; -> 换行符, &#x20; -> 空格

          return (
            <div className="wechat-link-card">
              <div className="link-content">
                <div className="link-desc" style={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>
                  {solitaireTitle}
                </div>
              </div>
            </div>
          );
        } catch (error) {
          console.error('解析接龙消息失败:', error);
          return (
            <Space>
              <Text type="secondary">[接龙]</Text>
            </Space>
          );
        }
      case 268445456: // 撤回消息（这个case不会被执行，因为在消息列表渲染时已经特殊处理）
        return null;
      case 268445458: // 撤回消息（这个case不会被执行，因为在消息列表渲染时已经特殊处理）
        return null;
      case 10000: // 添加好友消息（这个case不会被执行，因为在消息列表渲染时已经特殊处理）
        return null;
      default:
        return <Text type="secondary">[消息类型{msg.type}未处理]</Text>;
    }
  };

  // 格式化群组显示名称
  // contactNickname: 来自 wechat_contact 接口的 nickname
  // chatroom: 来自 wechat_chatroom 接口的详细信息
  const formatChatroomTitle = (chatroom: WechatChatroomDTO, contactNickname?: string) => {
    // 判断是否是群聊：chatroomname 包含 @chatroom
    const isChatroom = chatroom.chatroomname?.includes('@chatroom');
    
    if (!isChatroom) {
      // 不是群聊，直接返回显示名称
      return chatroom.displayname || chatroom.chatroomname;
    }
    
    // 是群聊，处理成员列表（用分号分隔）
    const memberIds = chatroom.memberlist?.split(';').filter(id => id.trim()) || [];
    const memberCount = memberIds.length;
    
    // 获取成员昵称
    const memberNames = memberIds
      .map(memberId => {
        const contact = allContactsData?.find(c => c.username === memberId.trim());
        if (contact) {
          // 如果有 alias，显示为 nickname（alias），否则只显示 nickname
          if (contact.alias && contact.nickname) {
            return `${contact.nickname}（${contact.alias}）`;
          } else {
            return contact.nickname || contact.alias || memberId.trim();
          }
        }
        return memberId.trim();
      })
      .filter(name => name);
    
    const membersText = memberNames.join('，');
    
    // 优先使用 wechat_contact 的 nickname
    const hasNickname = contactNickname && 
                       contactNickname.trim() !== '' && 
                       contactNickname !== 'null';
    
    if (hasNickname) {
      // 有 nickname，显示格式：nickname - (X人)成员1，成员2...
      return `${contactNickname} - (${memberCount}人)${membersText}`;
    } else {
      // 没有 nickname，只显示人数和成员列表：(X人)成员1，成员2...
      return `(${memberCount}人)${membersText}`;
    }
  };

  // 合并联系人和群组列表
  // contacts 包含所有联系人（包括群聊的基础信息）
  // chatrooms 包含群聊的详细信息
  // 通过 contact.username === chatroom.chatroomname 进行关联
  const contactBasedList = (allContactsData || []).map(contact => {
    // 判断是否是群聊（username 包含 @chatroom）
    const isChatroom = contact.username?.includes('@chatroom');

    if (isChatroom) {
      // 从 chatrooms 中找到对应的详细信息
      const chatroomDetail = chatrooms?.find(
        chatroom => chatroom.chatroomname === contact.username
      );

      if (chatroomDetail) {
        // 合并群聊的详细信息
        return {
          ...contact,
          ...chatroomDetail,
          type: 'chatroom' as const,
          // 左侧列表显示原始的 nickname
          nickname: contact.nickname || contact.username,
          // 保存完整的格式化标题，用于右侧详情显示
          fullTitle: formatChatroomTitle(chatroomDetail, contact.nickname),
          // 保留原始的 contact 数据，以便后续使用
          originalContact: contact,
        };
      } else {
        // 没有找到详细信息，使用基础信息
        return {
          ...contact,
          type: 'chatroom' as const,
          nickname: contact.nickname || contact.username,
        };
      }
    } else {
      // 普通联系人
      return {
        ...contact,
        type: 'contact' as const,
        nickname: contact.alias || contact.nickname,
      };
    }
  });

  // 补充 wechat_chatroom 中存在但 wechat_contact 中没有的群聊
  const contactUsernames = new Set((allContactsData || []).map(c => c.username));
  const normalizedKeyword = contactsKeyword.trim().toLowerCase();
  const extraChatrooms = (chatrooms || [])
    .filter(chatroom => {
      if (contactUsernames.has(chatroom.chatroomname)) return false;
      if (!normalizedKeyword) return true;
      const searchable = `${chatroom.chatroomnick || ''} ${chatroom.displayname || ''} ${chatroom.chatroomname || ''}`.toLowerCase();
      return searchable.includes(normalizedKeyword);
    })
    .map(chatroom => ({
      ...chatroom,
      username: chatroom.chatroomname,
      type: 'chatroom' as const,
      nickname: chatroom.chatroomnick || chatroom.displayname || chatroom.chatroomname,
      fullTitle: formatChatroomTitle(chatroom),
    }));

  const allContacts = [...contactBasedList, ...extraChatrooms];

  // 过滤掉原始 type === 4 和 type === 0 的联系人，用于左侧列表显示
  // 但保留完整的 allContacts 和 contacts 数据，用于群聊消息的发送者信息渲染
  const displayContacts = allContacts.filter(contact => {
    // 如果是群聊类型，直接显示
    if (contact.type === 'chatroom') return true;
    // 如果是联系人类型，检查原始 contact 的 type 字段
    // type === 4 和 type === 0 的联系人不在列表中显示
    const originalContact = allContactsData?.find(c => c.id === contact.id);
    return originalContact?.type !== 4 && originalContact?.type !== 0;
  });

  return (
    <div className="audit-manage-container">
      <Row gutter={16} style={{ height: '100%' }}>
        {/* 左侧：用户列表 */}
        <Col span={6} className="audit-left-panel">
          <AuditUserList
            selectedUserId={selectedUser?.id ? Number(selectedUser.id) : undefined}
            onUserSelect={handleUserClick}
            disabled={loadingWxUsers}
          />
        </Col>

        {/* 右侧：微信账号Tab + 联系人列表 + 消息列表 */}
        <Col span={18} style={{ height: '100%' }}>
          <div className="wechat-right-panel-loading">
            <Spin spinning={loadingWxUsers} size="large">
              {!selectedUser || !wxUsers || wxUsers.length === 0 ? (
                <div className="audit-empty-state">
                  <Empty description={!selectedUser ? "请选择左侧人员" : "暂无微信账号"} />
                </div>
              ) : (
                <Card
                  className="audit-card"
                  bodyStyle={{ padding: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
                  style={{ borderRadius: '8px', overflow: 'hidden' }}
                >
                  <Tabs
                    activeKey={activeWxTab}
                    onChange={handleWxTabChange}
                    style={{ height: '100%' }}
                  >
                    {/* 为每个微信账号创建一个Tab */}
                    {wxUsers.map((wxUser) => {
                      const isRefreshing = refreshingTabs.has(wxUser.wxid);
                      return (
                        <TabPane
                          tab={
                            <Space 
                              size={4}
                              style={{ 
                                cursor: isRefreshing ? 'not-allowed' : 'default',
                                userSelect: 'none'
                              }}
                            >
                              {isRefreshing && (
                                <ReloadOutlined 
                                  spin
                                  style={{ 
                                    fontSize: 12,
                                    color: '#1890ff'
                                  }}
                                />
                              )}
                              <span 
                                onClick={(e) => {
                                  e.stopPropagation(); // 阻止触发Tab切换
                                  if (!isRefreshing) {
                                    handleRefreshTabData(e, wxUser.wxid);
                                  }
                                }}
                                style={{ 
                                  opacity: isRefreshing ? 0.6 : 1,
                                  cursor: isRefreshing ? 'not-allowed' : 'pointer',
                                  pointerEvents: isRefreshing ? 'none' : 'auto'
                                }}
                              >
                                {wxUser.nickname}
                              </span>
                              <ReloadOutlined
                                onClick={(e) => {
                                  e.stopPropagation(); // 阻止触发Tab切换
                                  handleRefreshTabData(e, wxUser.wxid);
                                }}
                                spin={isRefreshing}
                                style={{
                                  fontSize: 12,
                                  cursor: isRefreshing ? 'not-allowed' : 'pointer',
                                  opacity: isRefreshing ? 0.6 : 1,
                                  pointerEvents: isRefreshing ? 'none' : 'auto'
                                }}
                              />
                              <Popconfirm
                                title="确认删除该微信账号？"
                                onConfirm={(e) => {
                                  e?.stopPropagation();
                                  if (!selectedUser) return;
                                  deleteWechatUser(company.id, wxUser.id).then(() => {
                                    message.success('删除成功');
                                    loadWxUsers(selectedUser.id as number);
                                    setSelectedWxUser(null);
                                    setActiveWxTab('');
                                  }).catch((err: any) => {
                                    message.error(err?.message || '删除失败');
                                  });
                                }}
                                onCancel={(e) => e?.stopPropagation()}
                                okText="确认"
                                cancelText="取消"
                              >
                                <DeleteOutlined
                                  onClick={(e) => e.stopPropagation()}
                                  style={{
                                    fontSize: 12,
                                    cursor: 'pointer',
                                    color: '#ff4d4f',
                                  }}
                                />
                              </Popconfirm>
                            </Space>
                          }
                          key={wxUser.wxid}
                          style={{ height: '100%' }}
                          disabled={isRefreshing}
                        >
                        <Row style={{ height: '100%' }}>
                          {/* 联系人列表（包含群组） */}
                          <Col span={10} style={{ borderRight: '1px solid #f0f0f0', height: '100%', display: 'flex', flexDirection: 'column' }}>
                            {/* 搜索框 */}
                            <div style={{ padding: '10px', borderBottom: '1px solid #f0f0f0' }}>
                              <Search
                                placeholder="搜索联系人"
                                allowClear
                                defaultValue={contactsKeyword}
                                onSearch={(value) => {
                                  setContactsKeyword(value);
                                  if (selectedUser && selectedWxUser) {
                                    loadContacts(selectedUser.id as number, selectedWxUser.wxid, value);
                                  }
                                }}
                                style={{ width: '100%' }}
                              />
                            </div>
                            <div
                              ref={contactListRef}
                              className="wechat-list-container"
                              style={{ flex: 1, overflowY: 'auto' }}
                              onScroll={(e) => {
                                const container = e.currentTarget;
                                const { scrollTop, scrollHeight, clientHeight } = container;
                                handleContactListScroll(scrollTop, scrollHeight, clientHeight);
                              }}
                            >
                              <Spin spinning={loadingContacts || loadingChatrooms}>
                                {displayContacts.length === 0 ? (
                                  <Empty
                                    description="暂无联系人"
                                    style={{ marginTop: '50%' }}
                                  />
                                ) : (
                                  <List
                                    dataSource={displayContacts}
                                    renderItem={(item) => {
                                      const isActive = item.type === 'contact' 
                                        ? selectedContact?.id === item.id 
                                        : selectedChatroom?.id === item.id;
                                      
                                      const handleClick = () => {
                                        if (item.type === 'contact') {
                                          // 找到原始的联系人对象
                                      const originalContact = allContactsData?.find(c => c.id === item.id);
                                      if (originalContact) {
                                        handleContactClick(originalContact);
                                      }
                                    } else {
                                      // 找到原始的群组对象
                                      const originalChatroom = chatrooms?.find(r => r.id === item.id);
                                      if (originalChatroom) {
                                        handleChatroomClick(originalChatroom);
                                      }
                                    }
                                  };
                                  
                                  // 统一显示为联系人样式
                                  // 如果有 alias，显示为 nickname（alias），否则只显示 nickname
                                  let displayTitle = item.nickname;
                                  if (item.type === 'contact') {
                                    const originalContact = allContactsData?.find(c => c.id === item.id);
                                    if (originalContact?.alias && originalContact?.nickname) {
                                      // 如果有 alias，显示为 nickname（alias）
                                      displayTitle = `${originalContact.nickname}（${originalContact.alias}）`;
                                    } else {
                                      // 没有 alias，只显示 nickname
                                      displayTitle = originalContact?.nickname || item.nickname;
                                    }
                                  }
                                  
                                  return (
                                    <List.Item
                                      className={`audit-list-item ${isActive ? 'active' : ''}`}
                                      onClick={handleClick}
                                    >
                                      <List.Item.Meta
                                        avatar={renderChatroomAvatar(item)}
                                        title={
                                          <div 
                                            className="contact-title-ellipsis" 
                                            title={displayTitle}
                                          >
                                            {displayTitle}
                                          </div>
                                        }
                                        description={
                                          <div
                                            className="contact-username-hover"
                                            style={{
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              whiteSpace: 'nowrap',
                                              maxWidth: '100%',
                                            }}
                                            title={item.username}
                                          >
                                            {item.username}
                                          </div>
                                        }
                                      />
                                    </List.Item>
                                  );
                                }}
                              />
                            )}
                          </Spin>
                        </div>
                      </Col>

                      {/* 消息列表 */}
                      <Col span={14} style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        {/* 消息列表头部 */}
                        {(selectedContact || selectedChatroom) && (
                          <div 
                            style={{ 
                              padding: '12px 16px', 
                              borderBottom: '1px solid #f0f0f0',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              background: '#fafafa'
                            }}
                          >
                            {(() => {
                              // 如果是群聊，从allContacts中查找完整信息
                              if (selectedChatroom) {
                                const chatroomInList = allContacts.find(
                                  item => item.type === 'chatroom' && item.id === selectedChatroom.id
                                );
                                
                                if (chatroomInList && (chatroomInList as any).fullTitle) {
                                  // 获取群成员信息
                                  const memberIds = (chatroomInList as any).memberlist?.split(';').filter((id: string) => id.trim()) || [];
                                  const members = memberIds.map((memberId: string) => {
                                    const contact = allContactsData?.find(c => c.username === memberId.trim());
                                    let nickname = memberId.trim();
                                    if (contact) {
                                      // 如果有 alias，显示为 nickname（alias），否则只显示 nickname
                                      if (contact.alias && contact.nickname) {
                                        nickname = `${contact.nickname}（${contact.alias}）`;
                                      } else {
                                        nickname = contact.nickname || contact.alias || memberId.trim();
                                      }
                                    }
                                    return {
                                      username: memberId.trim(),
                                      nickname: nickname,
                                      avatar: contact?.avatar,
                                    };
                                  });
                                  
                                  return (
                                    <Space size={8} align="center">
                                      <Text strong>
                                        {chatroomInList.nickname || selectedChatroom.displayname}
                                      </Text>
                                    <Popover 
                                      content={
                                        <div style={{ maxWidth: 400 }}>
                                          <div style={{ marginBottom: 12 }}>
                                              <Text strong>群聊成员1 ({members.length}人)</Text>
                                          </div>
                                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                            {members.map((member: { username: string; nickname: string; avatar?: string }, index: number) => (
                                              <div 
                                                key={index}
                                                style={{ 
                                                  display: 'flex', 
                                                  alignItems: 'center',
                                                  padding: '4px 8px',
                                                  background: '#f5f5f5',
                                                  borderRadius: '4px',
                                                  minWidth: '120px'
                                                }}
                                              >
                                                <Avatar 
                                                  src={member.avatar} 
                                                  icon={<UserOutlined />}
                                                  size="small"
                                                  style={{ marginRight: 6 }}
                                                  {...({ referrerPolicy: 'no-referrer' } as any)}
                                                />
                                                <Text ellipsis style={{ flex: 1, fontSize: 12 }}>
                                                  {member.nickname}
                                                </Text>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      }
                                      title="群聊详细信息"
                                      trigger="hover"
                                      overlayStyle={{ maxWidth: 450 }}
                                    >
                                        <EyeOutlined 
                                          style={{ 
                                            fontSize: 16, 
                                            color: '#1890ff', 
                                            cursor: 'pointer',
                                            transition: 'all 0.3s'
                                          }}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.color = '#40a9ff';
                                            e.currentTarget.style.transform = 'scale(1.1)';
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.color = '#1890ff';
                                            e.currentTarget.style.transform = 'scale(1)';
                                          }}
                                        />
                                    </Popover>
                                    </Space>
                                  );
                                }
                                return <Text strong>{selectedChatroom.displayname}</Text>;
                              }
                              
                              // 普通联系人
                              // 如果有 alias，显示为 nickname（alias），否则只显示 nickname
                              const contactDisplayName = selectedContact?.alias && selectedContact?.nickname
                                ? `${selectedContact.nickname}（${selectedContact.alias}）`
                                : (selectedContact?.nickname || selectedContact?.alias || '');
                              return (
                                <Text strong>
                                  {contactDisplayName}
                                </Text>
                              );
                            })()}
                            <Space>
                              {company.hasPermission('system:wechat:message-delete') && (
                                <>
                                  <Button
                                    danger
                                    size="small"
                                    icon={<DeleteOutlined />}
                                    title="按 msgId 批量删除指定消息"
                                    onClick={() => setBatchDeleteModalOpen(true)}
                                  >
                                    批量删除
                                  </Button>
                                  <Popconfirm
                                    title="清空当前对话的全部聊天记录？"
                                    description="将下发指令到客户端删除该联系人/群聊在微信本地数据库中的所有 message 记录，不可恢复"
                                    onConfirm={handleDeleteMessagesByTalker}
                                    okText="确认清空"
                                    cancelText="取消"
                                    okButtonProps={{ danger: true }}
                                  >
                                    <Button
                                      danger
                                      size="small"
                                      icon={<DeleteOutlined />}
                                      title="清空当前对话的全部聊天记录"
                                    >
                                      清空对话
                                    </Button>
                                  </Popconfirm>
                                </>
                              )}
                              {company.hasPermission('system:wechat:message-delete-all') && (
                                <Popconfirm
                                  title="清空整个微信号的全部消息？"
                                  description="将下发指令到客户端删除该微信号本地数据库中所有 message 记录，影响所有联系人与群聊，不可恢复"
                                  onConfirm={handleDeleteAllMessages}
                                  okText="确认清空"
                                  cancelText="取消"
                                  okButtonProps={{ danger: true }}
                                >
                                  <Button
                                    danger
                                    size="small"
                                    icon={<DeleteOutlined />}
                                    title="清空整个微信号本地数据库的全部消息"
                                  >
                                    清空全部
                                  </Button>
                                </Popconfirm>
                              )}
                              <Tooltip title="刷新消息">
                                <Button
                                  type="primary"
                                  size="small"
                                  icon={<ReloadOutlined />}
                                  onClick={handleRefreshMessages}
                                  loading={loadingMessages}
                                  style={{
                                    backgroundColor: '#07c160',
                                    borderColor: '#07c160'
                                  }}
                                >
                                  刷新
                                </Button>
                              </Tooltip>
                            </Space>
                          </div>
                        )}
                        
                        <div
                          ref={messageContainerRef}
                          className="wechat-message-container"
                          style={{
                            padding: '16px',
                            flex: 1,
                            overflowY: 'auto',
                            display: 'flex',
                            flexDirection: 'column',
                          }}
                        >
                          <Spin spinning={loadingMessages}>
                            {!selectedContact && !selectedChatroom ? (
                              <Empty description="请选择左侧联系人查看消息" />
                            ) : !messages || messages.length === 0 ? (
                              <Empty description="暂无消息记录" />
                            ) : (
                              <div className="message-list">
                                {/* 加载更多提示 */}
                                {loadingMore && (
                                  <div style={{ 
                                    textAlign: 'center', 
                                    padding: '12px',
                                    color: '#999',
                                    fontSize: '12px'
                                  }}>
                                    <Spin size="small" /> 加载更多消息...
                                  </div>
                                )}
                                {!hasMore && messages.length > 0 && (
                                  <div style={{ 
                                    textAlign: 'center', 
                                    padding: '12px',
                                    color: '#999',
                                    fontSize: '12px'
                                  }}>
                                    没有更多消息了
                                  </div>
                                )}
                                {[...messages].reverse().map((msg: WechatMessageDTO) => {
                                  // 获取消息发送者头像和联系人信息
                                  let avatarSrc = undefined;
                                  let senderContact: WechatContactDTO | null | undefined = null;

                                  if (msg.isSend === 1) {
                                    // 发送的消息，显示当前微信账号的头像
                                    avatarSrc = selectedWxUser?.avatar;
                                  } else {
                                    // 接收的消息
                                    if (selectedContact) {
                                      // 私聊，显示联系人头像
                                      senderContact = selectedContact;
                                      avatarSrc = selectedContact.avatar;
                                    } else if (selectedChatroom) {
                                      // 群聊，解析发送者并找到对应头像
                                      const { senderContact: parsedContact } = parseGroupMessage(msg.content);
                                      if (parsedContact) {
                                        senderContact = parsedContact;
                                        avatarSrc = parsedContact.avatar;
                                      }
                                    }
                                  }
                                  
                                  // 格式化显示名称
                                  const displayName = msg.isSend === 1
                                    ? '我'
                                    : formatContactName(senderContact) || '成员';

                                  // 撤回消息特殊处理
                                  if (msg.type === 268445456) {
                                    // 268445456: 居中显示，不显示头像和气泡
                                    return (
                                      <div
                                        key={msg.id}
                                        style={{
                                          textAlign: 'center',
                                          padding: '8px 0',
                                        }}
                                      >
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                          {msg.content || '你撤回了一条消息'}
                                        </Text>
                                      </div>
                                    );
                                  }

                                  // 268445458: 显示消息内容，但标识为已撤回
                                  if (msg.type === 268445458) {
                                    // 判断是否是当前用户撤回的消息
                                    // 如果 talker 包含 @chatroom，说明是群聊中当前用户撤回的消息，应该显示在右侧
                                    const isCurrentUserRecall = msg.talker && msg.talker.includes('@chatroom');
                                    const effectiveIsSend = isCurrentUserRecall ? 1 : msg.isSend;

                                    // 解析群聊消息内容（如果是群聊）
                                    let displayContent = msg.content;
                                    if (selectedChatroom && !isCurrentUserRecall) {
                                      const { message } = parseGroupMessage(msg.content);
                                      displayContent = message;
                                    }

                                    // 如果是当前用户撤回的消息，使用当前用户的头像和名称
                                    const recallAvatarSrc = isCurrentUserRecall ? selectedWxUser?.avatar : avatarSrc;
                                    const recallDisplayName = isCurrentUserRecall ? '我' : displayName;

                                    return (
                                      <div
                                        key={msg.id}
                                        className={`message-item ${
                                          effectiveIsSend === 1
                                            ? 'message-send'
                                            : 'message-receive'
                                        }`}
                                      >
                                        <div className="message-header" style={{ display: 'flex', alignItems: 'flex-start' }}>
                                          <Avatar
                                            src={recallAvatarSrc}
                                            icon={<UserOutlined />}
                                            size={36}
                                            style={{ marginRight: 8, flexShrink: 0 }}
                                            {...({ referrerPolicy: 'no-referrer' } as any)}
                                          />
                                          <div>
                                            <Text
                                              type="secondary"
                                              style={{
                                                textAlign: effectiveIsSend === 1 ? 'right' : 'left',
                                                fontSize: 12,
                                                display: 'block',
                                                whiteSpace: 'nowrap'
                                              }}
                                            >
                                              {recallDisplayName} •{' '}
                                              {dayjs(msg.createTime).format(
                                                'YYYY-MM-DD HH:mm:ss',
                                              )}
                                            </Text>
                                            <div className="message-bubble" style={{ marginTop: 4 }}>
                                              <Text>{displayContent}</Text>
                                            </div>
                                            <div style={{
                                              marginTop: 4,
                                              fontSize: 11,
                                              color: '#999',
                                              fontStyle: 'italic'
                                            }}>
                                              该内容为已撤回的内容
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  }

                                  // 添加好友消息特殊处理：居中显示，不显示头像和气泡
                                  if (msg.type === 10000) {
                                    // 检查是否是红包领取消息
                                    const isHongbaoMessage = msg.content && msg.content.includes('SystemMessages_HongbaoIcon.png');

                                    if (isHongbaoMessage) {
                                      // 解析红包领取消息内容
                                      // 格式：<img src="SystemMessages_HongbaoIcon.png"/>  cloverstd领取了你的<_wc_custom_link_...>红包</_wc_custom_link_>
                                      let hongbaoText = msg.content;

                                      // 移除 <img> 标签
                                      hongbaoText = hongbaoText.replace(/<img[^>]*>/g, '');
                                      // 移除 <_wc_custom_link_> 标签，保留内容
                                      hongbaoText = hongbaoText.replace(/<\/_wc_custom_link_>/g, '');
                                      hongbaoText = hongbaoText.replace(/<_wc_custom_link_[^>]*>/g, '');
                                      // 去除多余空格
                                      hongbaoText = hongbaoText.trim();

                                      return (
                                        <div
                                          key={msg.id}
                                          style={{
                                            textAlign: 'center',
                                            padding: '8px 0',
                                          }}
                                        >
                                          <Space size={4}>
                                            <GiftOutlined style={{ color: '#FD9931', fontSize: 14 }} />
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                              {hongbaoText}
                                            </Text>
                                          </Space>
                                        </div>
                                      );
                                    }

                                    // 检查是否是转账待领取消息
                                    const isTransferPendingMessage = msg.content && msg.content.includes('待接收的') && msg.content.includes('转账');

                                    if (isTransferPendingMessage) {
                                      // 解析转账待领取消息内容
                                      // 格式：你有一笔待接收的<_wc_custom_link_...>转账</_wc_custom_link_>
                                      let transferText = msg.content;

                                      // 移除 <_wc_custom_link_> 标签，保留内容
                                      transferText = transferText.replace(/<\/_wc_custom_link_>/g, '');
                                      transferText = transferText.replace(/<_wc_custom_link_[^>]*>/g, '');
                                      // 去除多余空格
                                      transferText = transferText.trim();

                                      return (
                                        <div
                                          key={msg.id}
                                          style={{
                                            textAlign: 'center',
                                            padding: '8px 0',
                                          }}
                                        >
                                          <Space size={4}>
                                            <TransactionOutlined style={{ color: '#FD9931', fontSize: 14 }} />
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                              {transferText}
                                            </Text>
                                          </Space>
                                        </div>
                                      );
                                    }

                                    // 检查是否是转账过期消息
                                    const isTransferExpiredMessage = msg.content && msg.content.includes('转账') && msg.content.includes('已过期');

                                    if (isTransferExpiredMessage) {
                                      // 解析转账过期消息内容
                                      // 格式：你有一笔<_wc_custom_link_...>转账</_wc_custom_link_>24小时内未接收，已过期
                                      let expiredText = msg.content;

                                      // 移除 <_wc_custom_link_> 标签，保留内容
                                      expiredText = expiredText.replace(/<\/_wc_custom_link_>/g, '');
                                      expiredText = expiredText.replace(/<_wc_custom_link_[^>]*>/g, '');
                                      // 去除多余空格
                                      expiredText = expiredText.trim();

                                      return (
                                        <div
                                          key={msg.id}
                                          style={{
                                            textAlign: 'center',
                                            padding: '8px 0',
                                          }}
                                        >
                                          <Space size={4}>
                                            <TransactionOutlined style={{ color: '#999', fontSize: 14 }} />
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                              {expiredText}
                                            </Text>
                                          </Space>
                                        </div>
                                      );
                                    }

                                    // 普通系统消息
                                    return (
                                      <div
                                        key={msg.id}
                                        style={{
                                          textAlign: 'center',
                                          padding: '8px 0',
                                        }}
                                      >
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                          {msg.content}
                                        </Text>
                                      </div>
                                    );
                                  }

                                  // 语音/视频通话消息特殊处理：居中显示，不显示头像和气泡
                                  if (msg.type === 64) {
                                    return (
                                      <div
                                        key={msg.id}
                                        style={{
                                          textAlign: 'center',
                                          padding: '8px 0',
                                        }}
                                      >
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                          {msg.content}
                                        </Text>
                                      </div>
                                    );
                                  }

                                  return (
                                    <div
                                      key={msg.id}
                                      className={`message-item ${
                                        msg.isSend === 1
                                          ? 'message-send'
                                          : 'message-receive'
                                      }`}
                                    >
                                      <div className="message-header" style={{ display: 'flex', alignItems: 'flex-start' }}>
                                        <Avatar
                                          src={avatarSrc}
                                          icon={<UserOutlined />}
                                          size={36}
                                          style={{ marginRight: 8, flexShrink: 0 }}
                                          {...({ referrerPolicy: 'no-referrer' } as any)}
                                        />
                                        <div>
                                          <Text
                                            type="secondary"
                                            style={{
                                              textAlign: msg.isSend === 1 ? 'right' : 'left',
                                              fontSize: 12,
                                              display: 'block',
                                              whiteSpace: 'nowrap'
                                            }}
                                          >
                                            {displayName} •{' '}
                                            {dayjs(msg.createTime).format(
                                              'YYYY-MM-DD HH:mm:ss',
                                            )}
                                          </Text>
                                          <div className="message-bubble" style={{ marginTop: 4 }}>
                                            {renderMessageContent(msg)}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </Spin>
                        </div>
                      </Col>
                    </Row>
                  </TabPane>
                      );
                    })}
              </Tabs>
            </Card>
          )}
            </Spin>
          </div>
        </Col>
      </Row>

      {/* 视频播放器 Modal */}
      <Modal
        title="视频播放"
        open={videoPlayerVisible}
        onCancel={() => {
          setVideoPlayerVisible(false);
          setVideoPlayerUrl('');
        }}
        footer={null}
        width={800}
        centered
      >
        {videoPlayerUrl && (
          <video
            controls
            autoPlay
            style={{ width: '100%', maxHeight: '500px' }}
            src={videoPlayerUrl}
          >
            您的浏览器不支持视频播放
          </video>
        )}
      </Modal>

      {/* 聊天记录查看器 Modal */}
      <Modal
        title={
          <Space>
            <MessageOutlined style={{ color: '#07c160' }} />
            <span>{chatRecordData?.title || '聊天记录'}</span>
          </Space>
        }
        open={chatRecordVisible}
        onCancel={() => {
          setChatRecordVisible(false);
          setChatRecordData(null);
        }}
        footer={null}
        width={600}
        centered
        className="chat-record-modal"
      >
        {chatRecordData && (
          <div className="chat-record-viewer">
            <div className="chat-record-info">
              <Text type="secondary">
                {chatRecordData.isChatRoom ? '群聊' : '私聊'} · 共 {chatRecordData.count} 条消息
              </Text>
            </div>
            <div className="chat-record-list">
              {chatRecordData.records.map((record: any, index: number) => (
                <div key={index} className="chat-record-item">
                  <div className="record-user">
                    <Avatar 
                      src={record.sourceheadurl} 
                      size={32}
                      icon={<UserOutlined />}
                      {...({ referrerPolicy: 'no-referrer' } as any)}
                    />
                    <div className="record-user-info">
                      <Text strong>{record.sourcename}</Text>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {record.sourcetime}
                      </Text>
                    </div>
                  </div>
                  <div className="record-content">
                    <Text>{record.content}</Text>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal>

      <Modal
        title="批量删除消息"
        open={batchDeleteModalOpen}
        onCancel={() => setBatchDeleteModalOpen(false)}
        onOk={handleBatchDeleteByIds}
        okText="下发删除"
        cancelText="取消"
        okButtonProps={{ danger: true, loading: batchDeleteSubmitting }}
        destroyOnClose
      >
        <div style={{ marginBottom: 8, color: '#666' }}>
          输入要删除的 msgId（每行一个，或用逗号分隔），将下发到客户端从微信本地数据库删除对应消息，不可恢复
        </div>
        <Input.TextArea
          rows={6}
          value={batchDeleteMsgIds}
          onChange={(e) => setBatchDeleteMsgIds(e.target.value)}
          placeholder="1234&#10;5678&#10;或：1234, 5678"
        />
      </Modal>
    </div>
  );
}

